/* eslint-disable max-classes-per-file */
import { ILogger } from 'app/logger/ILogger';
import { Copier } from './../base/copier';
import {
    A365Named,
    AdapterContext,
    Credential,
    Database,
    DatabaseEntity,
    DefaultIdentityProvider,
    eModelState,
    getIdentity,
    IAttribute,
    IIdentityProvider,
    IModelAdapter,
    IModelAdapterAsync,
    IOwnedViolation,
    KqlScript,
    LinkedService,
    LinkTopic,
    LoadingStatus,
    LoadingStatusEnum,
    ModelConstants,
    Notebook,
    ScopeJobDefinition,
    SparkJobDefinition,
    SqlPool,
    SqlScript
} from 'shell-models-lib';
import { DefaultPropertyInspector, IPropertyInspector } from './../base/walker';
import { IFullResource, IResourceFilesProvider } from 'app/interfaces/IResourceFilesProvider';
import { Inject, Injectable, inject, EnvironmentInjector } from '@angular/core';
import { Named } from '../base/named';
import { Metamodel } from '../base/model';
import { IModel } from '../base/annotation';
import { ModelMarker } from './state';
import {
    buildModelMarkerWrapperInContext,
    markBroken,
    markListed,
    markZombie
} from 'app/shared/utils/model-marker-wrapper';
import { BehaviorSubject, from, lastValueFrom, Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { BaseDataProviderService } from 'app/services/base-data-provider.service';
import { Direction, Walker } from '../base/walker';
import { IMutation, IMutationContext, IMutator, Mutation } from './imutator';
import { ResourceMutator } from './resource-mutator';
import { Shadow } from './shadow';
import { ModelUtilties } from './model-utilities';
import { Dependency } from './dependency';
import { ModelFetchUtils } from 'app/model/services/model.utils/model.fetch.utils';
import { IntegrationRuntime } from 'app/model/integrationRuntime/integrationRuntime';
import { ModelRegisterationUtils } from './model.utils/model.registration.utils';
import { ISaveResult, SaveStatus } from '../../interfaces/ISaveStatus';
import * as Constants from 'app/services/globals';
import { loggerConstants } from '../../logger/logger.constants';
import { DynamicConnectorService } from '../../services/dynamic-connector.service';
import { convertObjectToString, format, stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { PerformanceEvent } from 'app/logger/logger.performance-event';
import { LoggerVariables } from '../../logger/logger.common';
import { ReferencedProxyNamedInfo } from 'app/model/services/referenced-proxy-named-info';
import { WranglingDataflow } from '../dataflow/wranglingDataflow';
import { DataFlow } from '../dataflow/dataflow';
import { Pipeline } from '../pipeline/pipeline';
import { Dataset } from '../dataset/dataset';
import { AirflowEntity } from '../airflowEntity/airflowEntity';
import { Trigger } from '../trigger/trigger';
import { IFrameMessengerService } from 'app/services/iframe-messenger.service';
import { catchError, filter, map, mergeMap, scan, take, timeout } from 'rxjs/operators';
import { Factory } from '../factory/factory';

import { FactoryUrlInformationService } from 'app/services/factory-url-information.service';
import { IModelService } from './IModelService';
import ShellAPI from 'a365-shell-api';
import { IDataFlow } from '../adapters/dataflow/IDataflow';
import { setTimeout } from 'timers';
import { sleep } from 'app/shared/utils/sleep.util';
import { Flowlet } from '../dataflow/flowlet';
import { IParentResource, ResourceFilesUtils } from 'app/shared/utils/resourcefiles.utils';
import { isEqual, isMap, isSet } from 'lodash';
import { UserDefinedFunctionLibrary } from '../dataflow/user-defined-function-library';
import { AutosaveShadow } from './autosave-shadow';
import { IFrameService } from 'app/services/iframe.service';
import { isDedicatedAdfInIframe } from 'app/services/iframe/iframe.utils';
import { DEDICATED_NOTFOUND_ENTITY_ERROR_TEMPLATE } from 'app/services/iframe-customer-profiles';
import { AdfDataMapper } from '../adfDataMapper/adfDataMapper';
import { IdentityService } from 'app/model/services/identity.service';
import { IResourceContext, RESOURCE_CONTEXT_DATA } from 'app/multi-contexts/resource-context.typings';

interface ISubjectChange {
    index: number; // monotonically increasing number
    subject: Named;
    added: boolean;
}

class FetchIdentityProvider implements IIdentityProvider {
    public identity(named: Named): string {
        const identity = getIdentity(named);
        if (!identity) {
            throw new Error(' Named resource should be identifiable');
        }
        return identity;
    }

    public name(resource: {}): string | undefined {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return !!resource ? (resource as any).name : undefined;
    }
}

// To avoid confusing customers, temporarily do not change the naming convention of a GA-ed product.
const namingPrefix = new Map<string, string>(
    __IS_A365__
        ? [
            [Metamodel.modelFor(Notebook).name, 'Notebook '],
            [Metamodel.modelFor(ScopeJobDefinition).name, 'SCOPE job definition '],
            [Metamodel.modelFor(Database).name, 'Database'], // Do not add a space here, as Database name can't contain spaces
            [Metamodel.modelFor(SqlScript).name, 'SQL script '],
            [Metamodel.modelFor(KqlScript).name, 'KQL script '],
            [Metamodel.modelFor(SparkJobDefinition).name, 'Spark job definition '],
            [Metamodel.modelFor(DataFlow).name, 'Dataflow'],
            [Metamodel.modelFor(LinkedService).name, 'Linked service '],
            [Metamodel.modelFor(Trigger).name, 'Trigger '],
            [Metamodel.modelFor(IntegrationRuntime).name, 'IntegrationRuntime'],
            [Metamodel.modelFor(Dataset).name, 'Dataset '],
            [Metamodel.modelFor(Pipeline).name, 'Pipeline '],
            [Metamodel.modelFor(Credential).name, 'Credential'],
            [Metamodel.modelFor(LinkTopic).name, 'linkconnection'],
            [Metamodel.modelFor(AirflowEntity).name, 'AirflowEntity ']
        ]
        : []
);

// With delay set to 5ms, CPU usage is about 50% with AdfLarge2 factory. It's around 70% when set to 0ms
const fetchDelayMs = 5;

@Injectable({
    providedIn: 'root'
})
export class ModelService implements IModelService {
    // For quick lookup of named model
    public static readonly namedModel: IModel = Metamodel.modelFor(Named);

    public static readonly dependencyProperty = ['__dependencies__'];

    public static readonly MAX_SUBJECT_COUNT = 15000;

    protected static className: string = 'ModelService';

    public identityProvider: IIdentityProvider = {
        identity: (named: Named): string => {
            const identity = this._identityService.identity(named);
            if (!identity) {
                this.logger.error(Metamodel.modelOf(named).name, ' Named resource should be identifiable');
                throw new Error(' Named resource should be identifiable');
            }
            return identity;
        },
        name: (resource: {}): string | undefined => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return !!resource ? (resource as any).name : undefined;
        }
    };

    /**
     * Key = named's identifier; Value = set of nameds referencing the named in key
     * It will be used for validation during publishing time.
     */
    public referencedByMap: Map<string, Set<Named>> = new Map();

    /**
     * Updated once all resources have finished downloading, which will enable features like renaming.
     * If not all resources have been downloaded yet, isLoading will have a value of true.
     */
    public isLoading: BehaviorSubject<boolean> = new BehaviorSubject(true);

    /**
     * Start to update fetch progress (resource type being processed) once all resources have finished downloading.
     * Set to 0 when fetch is completed
     * If not all resources have been downloaded yet, isFetching will have a value of false.
     */
    public get isFetching(): Observable<boolean | number | string> {
        return this._isFetching;
    }

    public get deletedNameds(): Set<Named> {
        return this._deletedNameds;
    }

    public get modelAttributeContext() {
        return this._modelAttributeContext;
    }

    public get factoryName(): string {
        return this._urlInformationService?.resourceName;
    }

    private _isFetching: BehaviorSubject<boolean | number | string> = new BehaviorSubject(false);
    private _backgroundFetchTimerHandlers: NodeJS.Timeout[] = [];

    /*
        Returns the next subject index to use with subject changes
    */
    private _currentSubjectIndex = 0;

    /*
        All objects here are either listed or fetched. They could be new or dirty but
        after save they go back to fetched status
    */
    private idToObjectMap: Map<number, Named> = new Map<number, Named>();

    /**
     * Identity to object map improves the performance when searching for a named.
     * TODO: With introduction of identityToObjectMap, idToObjectMap can be removed.
     * But it will take careful refactoring.
     */
    private identityToObjectMap: Map<string, Named> = new Map<string, Named>();

    /*
        This represents the items that are committed to the ModelService
        but not yet saved into the metadata service. The commitNamedMap
        only allows new objects and those deleted henceforth. Fetched and dirty objects
        do not make sense to be committed since there is already a saved copy. The advantages
        of having a committed set is that listing API and unique name apis honor these entries
    */
    private idToCommittedObjectMap: Map<number, Named> = new Map<number, Named>();

    /**
     * Identity to committed object map improves the performance when searching for a named. With introduction
     * TODO: of identityToCommittedObjectMap, idToCommittedObjectMap can be removed. But it will take careful
     * refactoring.
     */
    private identityToCommittedObjectMap: Map<string, Named> = new Map<string, Named>();

    /*
        If we are waiting to fetch a object, we will return the same promise back to the caller
    */
    private idToPromiseFetchQueue: Map<number, Promise<Named>> = new Map<number, Promise<Named>>();

    /*
     * Record all the deleted nameds from model service while not yet published to Azure or committed to Git repo.
     * It's used to recover the deleted nameds when user discards all changes.
     */
    private _deletedNameds: Set<Named> = new Set<Named>();

    /*
     * Record the refresh status for different resource types
     */
    private _refreshStatusMap: Map<Constants.ResourceType, boolean> = new Map();

    // We push resources to replaySubject to keep history.
    // As Madrid doesn't support factory with more than 10000 resource, it's reasonable to only keep last 15000 values
    private subject = new ReplaySubject<ISubjectChange>(ModelService.MAX_SUBJECT_COUNT);

    private syncwait: Promise<void>;

    private saveAdapterContext: AdapterContext;

    private fetchAdapterContext: AdapterContext;
    private fastQueueFetchAdapterContext: AdapterContext;

    private propertyInspector: IPropertyInspector = {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getPropertyNames: (object: any): string[] => {
            /*
                This is primarily for modelled objects
            */
            const model = Metamodel.modelOf(object);
            if (
                !model.concept ||
                !ModelService.namedModel.isAncestor(model) ||
                !ModelMarker.isListed(object as Named)
            ) {
                return DefaultPropertyInspector.instance.getPropertyNames(object);
            }

            return ModelService.dependencyProperty;
        },

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        getPropertyValue: (object: any, property: string): any => {
            return property === ModelService.dependencyProperty[0]
                ? Dependency.dependencies(object as Named)
                : DefaultPropertyInspector.instance.getPropertyValue(object, property);
        }
    };

    /**
     * Subscription that links to the Observable containing all resources that have been downloaded
     */
    private _resourceSubscription: Subscription;

    private _factory: Factory;

    private get _resourceFilesProvider(): IResourceFilesProvider {
        return this.dataProviderService.getResourceFilesProvider();
    }

    private get logger(): ILogger {
        return this.dataProviderService.getLogger();
    }

    private _deletedResource$: BehaviorSubject<ShellAPI.resources.IRecentResourceId> =
        new BehaviorSubject<ShellAPI.resources.IRecentResourceId>(null);

    private _modelAttributeContext = {
        inlineDataset: !!this._iframeService.currentCustomerProfile?.inlineDataset
    };

    private _sharedAdapterContext = {
        ...this._modelAttributeContext,
        referenceById: !!this._iframeService.currentCustomerProfile?.referenceById,
        tolerateInvalidReference: !!this._iframeService.currentCustomerProfile?.tolerateInvalidReference,
        preProcessResource: this._iframeService.currentCustomerProfile?.preProcessResource,
        postProcessResource: this._iframeService.currentCustomerProfile?.postProcessResource
    };

    private _isDedicatedIFrame: boolean;
    private _currentEnvironmentInjector: EnvironmentInjector;

    // Service dependencies here should be added with care. ModelService should be able to operate
    // independently.
    constructor(
        @Inject(RESOURCE_CONTEXT_DATA) private _contextData: IResourceContext,
        readonly iframeMessengerService: IFrameMessengerService,
        private readonly _urlInformationService: FactoryUrlInformationService,
        readonly dataProviderService: BaseDataProviderService,
        private readonly _iframeService: IFrameService,
        private readonly _identityService: IdentityService,
        private readonly _dynamicConnectorService: DynamicConnectorService
    ) {
        this._currentEnvironmentInjector = inject(EnvironmentInjector);

        this._isDedicatedIFrame = isDedicatedAdfInIframe(this._contextData.contextType);
        this.saveAdapterContext = AdapterContext.newAdapterContext({
            contextData: _contextData,
            identityProvider: DefaultIdentityProvider.instance,
            ...this._sharedAdapterContext
        });

        const identityProvider = new FetchIdentityProvider();

        this.fetchAdapterContext = AdapterContext.newAdapterContext({
            contextData: _contextData,
            identityProvider
        });

        this.fastQueueFetchAdapterContext = AdapterContext.newAdapterContext({
            contextData: _contextData,
            identityProvider,
            fastQueue: true
        });

        this.syncwait = this.synchronize();
        this.isLoading.subscribe((isLoading: boolean) => {
            if (!isLoading) {
                LoggerVariables.resourceCountAfterLoading = this.identityToObjectMap.size;
            }
        });
    }

    public clone(
        contextData: IResourceContext,
        iframeMessengerService: IFrameMessengerService,
        urlInformationService: FactoryUrlInformationService,
        dataProviderService: BaseDataProviderService,
        iframeService: IFrameService,
        identityService: IdentityService,
        dynamicConnectorService: DynamicConnectorService
    ): ModelService {
        return this._currentEnvironmentInjector.runInContext(
            () =>
                new ModelService(
                    contextData,
                    iframeMessengerService,
                    urlInformationService,
                    dataProviderService,
                    iframeService,
                    identityService,
                    dynamicConnectorService
                )
        );
    }

    public get deletedResource$(): Observable<ShellAPI.resources.IRecentResourceId> {
        return this._deletedResource$;
    }

    public async initialize(): Promise<void> {
        return this.syncwait;
    }

    public get factory(): Factory {
        return this._factory;
    }

    private _modelMarkerWrapper = buildModelMarkerWrapperInContext();

    /*
        Lists all the linked services matching an optional regex and filtered by
        an optional set of types. The object may only have the shadow attributes(name, description) and
        need to be fetched to fill in remaining details
    */
    public list(regex?: RegExp, ...types: IModel[]): Observable<Named[]> {
        // Wait for the syncwait to proceed until we do anything
        return from(this.syncwait).pipe(
            mergeMap(() => {
                // We want to optimize getting the entries for things that already exist instead of making it work
                // one item at a time
                const changesNow: ISubjectChange[] = [];

                // Create a closure
                const currentSubjectIndex = this._currentSubjectIndex;

                const matches = (named: Named): boolean => {
                    if (types.length === 0 && (!regex || (named.name && named.name.match(regex)))) {
                        // Only list Factories if we explicitly ask for it, consider refactor of all callers if at some point we want to include it.
                        return !(named instanceof Factory);
                    } else {
                        for (const atype of types) {
                            if (atype.isInstance(named) && (!regex || (named.name && named.name.match(regex)))) {
                                return true;
                            }
                        }
                    }
                    return false;
                };

                // Add the current matched items into the array to bootstrap things faster
                const takeCount =
                    currentSubjectIndex > ModelService.MAX_SUBJECT_COUNT
                        ? ModelService.MAX_SUBJECT_COUNT
                        : currentSubjectIndex;
                this.subject.pipe(take(takeCount)).subscribe((change) => {
                    if (matches(change.subject)) {
                        if (!change.added) {
                            // A manual scan for now. Can be improvised later for large number of objects
                            const index = changesNow.findIndex((foundChange) => foundChange.subject === change.subject);
                            if (index >= 0) {
                                changesNow.splice(index, 1);
                            }
                        } else {
                            changesNow.push(change);
                        }
                    }
                });

                // leave the last one out since we need to force one item out of the observable
                // If list is empty it will still work
                const lastMatchedChange = changesNow.pop();
                const lastSubjectIndex = lastMatchedChange ? lastMatchedChange.index : undefined;

                return this.subject.pipe(
                    filter((change) => {
                        // Filter weed out things we don't want
                        if (!change.subject) {
                            return false;
                        }

                        if (change.index < currentSubjectIndex && change.index !== lastSubjectIndex) {
                            return false;
                        }

                        return matches(change.subject);
                    }),
                    map((change) => [change]), // Make this an change array
                    scan((prevChanges, changes) => {
                        // Aggregate changes across
                        if (changes[0].added) {
                            prevChanges.push(changes[0]);
                        } else {
                            const index = prevChanges.findIndex(
                                (prevChange) => prevChange.subject === changes[0].subject
                            );
                            if (index !== -1) {
                                prevChanges.splice(index, 1);
                            }
                        }

                        return prevChanges;
                    }, changesNow),
                    map((changes) =>
                        changes.map((change) => {
                            return change.subject;
                        })
                    )
                ); // Get the named only
            })
        );
    }

    /**
     * Fetch all nameds except on-demand fetched ones
     * @param nameds Nameds to fetch
     * @param force Set as true if on-demand fetched nameds should also be fetched, default to false
     */
    public async fetchAll<T extends Named>(nameds: T[], force: boolean = false): Promise<T[]> {
        if (nameds.length > this.identityToObjectMap.size / 2) {
            this.logger.log(
                ModelService.className,
                `fetchAll - start. Fetch named count: ${nameds.length}, total resource count: ${this.identityToObjectMap.size}`
            );
        }
        const promises = [];
        const namedsToFetch = !force ? this._excludeOndemandFetchedNameds(nameds) : nameds;
        for (const named of namedsToFetch) {
            promises.push(this._fetchInternal(this.fetchAdapterContext, named));
        }
        return Promise.all(promises);
    }

    public async fetchWithCustomResource<T extends Named>(named: T, customResource: string): Promise<T> {
        return this._fetchInternal(this.fetchAdapterContext, named, null, customResource);
    }

    public async ensureNotebookIntactAndFetchAll<T extends Named>(nameds: T[]): Promise<void> {
        if (nameds.length > this.identityToObjectMap.size / 2) {
            this.logger.log(
                ModelService.className,
                `fetchAllWithContent - start. Fetch named count: ${nameds.length}, total resource count: ${this.identityToObjectMap.size}`
            );
        }
        if (__IS_A365__) {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            const { MaxConcurrencyPromiseScheduler } = await import('app/utils/MaxConcurrencyPromiseScheduler')
            const refreshThrottler = new MaxConcurrencyPromiseScheduler(
                (message: string, properties?: Record<string, string | number>) => this.logger.log(ModelService.className, message, properties),
                50
            );
            this.logger.log(
                ModelService.className,
                `ensureNotebookIntactAndFetchAll - start. Fetch named count: ${nameds.length}, total resource count: ${this.identityToObjectMap.size}`
            );
            for (const named of nameds) {
                refreshThrottler.queue(async () => {
                    await this.fetch(named);
                });
            }
            await refreshThrottler.waitAll();
            this.logger.log(
                ModelService.className,
                `ensureNotebookIntactAndFetchAll - end. Fetch named count: ${nameds.length}, total resource count: ${this.identityToObjectMap.size}`
            );
        } else {
            const promises = [];
            for (const named of nameds) {
                promises.push(this.fetch(named));
            }
            await Promise.all(promises);
        }
    }

    /**
     * Fetches the details of the named if not already fetched.
     * force: In the normal case, only the listed named will be fetched, set this flag as true to fetch the named regardless the model state. This is useful when we want to re-fetch a fetched named
     */
    public async fetch<T extends Named>(named: T, force?: boolean): Promise<T> {
        const concept = Metamodel.modelOf(named).concept;
        const conceptName = concept ? concept.name : '';
        if (__IS_A365__ && conceptName === 'notebook' && !this.dataProviderService.isGitMode()) {
            const customResource = await this._fetchNotebookResource(named);
            return this._fetchInternal(this.fetchAdapterContext, named, null, customResource, force);
        }
        return this._fetchInternal(this.fetchAdapterContext, named, undefined, undefined, force);
    }

    /*
        Fetches the details of the named if not already fetched in fast queue, used in resource list
    */
    public async fetchFromFastQueue<T extends Named>(named: T): Promise<T> {
        return this._fetchInternal(this.fastQueueFetchAdapterContext, named);
    }

    /*
        Fetches the details of the Named by identity if not already fetched.
    */
    public async fetchByIdentity(
        modelIdentity: string,
        downloadIfNotPresent?: boolean,
        resourceIdentity?: string /**can be name or objectId */,
        options?: {
            workspaceObjectId?: string;
            resourceSubType?: string;
        }
    ): Promise<Named | undefined> {
        const named = await this._getNamedByIdentity(modelIdentity, downloadIfNotPresent, resourceIdentity, options);
        if (!named) {
            return undefined;
        }
        return this._fetchInternal(this.fetchAdapterContext, named);
    }

    /*
        Reverts this object back to the fetched state discarding any changes done to it.
        Any lifelines held to this object will be discarded as well. The unused hint tells the
        model service it is candidate to be reclaimedad
        If force is true, discard the save version and back to latest shadow version;
        Otherwise, just discard the local change
    */
    public revert<T extends Named>(nameds: T[], force: boolean = false): T[] {
        // If any of them do not have shadows we don't want to revert
        // We can ignore any lifeline objects anyway. Also we cannot revert new objects.
        // We cannot revert listed objects also
        const shadows: Named[] = [];
        const lifelines = new Array<Named>();
        const namedSet = new Set<Named>();
        for (let index = 0; index < nameds.length; index++) {
            const named = nameds[index];
            if (ModelMarker.isNew(named) || ModelMarker.isListed(named)) {
                this.logger.error(Metamodel.modelOf(named).name, `Cannot revert new/listed object ${named.name}`);
                throw new Error(`Cannot revert new/listed object ${named.name}`);
            }

            if (ModelMarker.lifeline(named)) {
                nameds.splice(index, 1);
                index--;
                lifelines.push(named);
                this._modelMarkerWrapper.markDelete(named);
                continue;
            }

            namedSet.add(named);
            let shadow = Shadow.shadow(named);

            if (Constants.GlobalVariables.enableAutosave) {
                const concept = Metamodel.modelOf(named).concept;
                const conceptName = concept ? concept.name : '';
                if (!force && Constants.GlobalVariables.isAutosaveSupportType(conceptName as Constants.ResourceType)) {
                    shadow = AutosaveShadow.shadow(named);
                }
            }

            if (!shadow) {
                this.logger.error(Metamodel.modelOf(named).name, `Cannot revert unshadowed object ${named.name}`);
                throw new Error(`Cannot revert unshadowed object ${named.name}`);
            }
            shadows.push(shadow);
        }

        Copier.clone(this._modelAttributeContext, shadows, nameds, false, Constants.GlobalVariables.enableAutosave);

        for (const named of nameds) {
            if (!Constants.GlobalVariables.enableAutosave) {
                this._modelMarkerWrapper.markFetched(named); // Get these guys back to the regular mode
            } else {
                const concept = Metamodel.modelOf(named).concept;
                const conceptName = concept ? concept.name : '';
                // for the named supported in autosave, let's respect the state stored in shadow
                if (!Constants.GlobalVariables.isAutosaveSupportType(conceptName as Constants.ResourceType)) {
                    this._modelMarkerWrapper.markFetched(named); // Get these guys back to the regular mode
                } else {
                    // emit changeType info which is used in other components, e.g. factory-options
                    const isFetched = ModelMarker.isFetched(named);
                    const isDirty = ModelMarker.isDirty(named);
                    if (isFetched) {
                        this._modelMarkerWrapper.markFetched(named);
                    }
                    if (isDirty) {
                        this._modelMarkerWrapper.markDirty(named);
                    }
                }
            }
        }

        /*
            Did any lifelines get scoped out? Let's check all lifeline objects and if any objects are in this list
            they have to go as well
        */
        for (const named of this.idToCommittedObjectMap.values()) {
            const lifeline = ModelMarker.lifeline(named);
            if (lifeline && namedSet.has(lifeline)) {
                this._modelMarkerWrapper.markDelete(named);
                lifelines.push(named);
            }
        }

        if (lifelines.length > 0) {
            this.commit(...lifelines);
        }

        return nameds;
    }

    /*
        Ungets the object if this object is no longer in use. Use only for external requests outside model service
    */
    public unfetch<T extends Named>(named: T): T {
        // You cannot unfetch objects that have in bound dependencies into it.
        // Verify and throw up if that is the case. You cannot unfetch a new object
        if (ModelMarker.isNew(named)) {
            this.logger.error(Metamodel.modelOf(named).name, 'Cannot unfetch a new object');
            throw new Error('Cannot unfetch a new object');
        }
        const others = new Array<Named>();
        this.idToObjectMap.forEach((value) =>
            value !== named && !ModelMarker.isListed(value) && !ModelMarker.isDeleted(value)
                ? others.push(value)
                : undefined
        );

        this.idToCommittedObjectMap.forEach((value) =>
            !ModelMarker.isDeleted(value) ? others.push(value) : undefined
        );

        if (Walker.conceptDependencies(this._modelAttributeContext, this.propertyInspector, ...others).has(named)) {
            this.logger.error(Metamodel.modelOf(named).name, 'Cannot unfetch a object that other objects depend on');
            throw new Error('Cannot unfetch a object that other objects depend on');
        }

        // When you unfetch you have to lose the lifeline objects directly and indirectly. Commit takes care of deleting indirect ones
        const lifelines = new Array<Named>();
        const dependencies = new Set<Named>(); // This is anything other than the lifelines
        Walker.directConceptDependencies(this._modelAttributeContext, named).forEach((value) =>
            ModelMarker.lifeline(value) === named ? lifelines.push(value) : dependencies.add(value)
        );
        // Clear only after finding dependencies!
        named.clear();
        markListed(named);
        // Reattach the dependencies
        if (dependencies.size > 0) {
            Dependency.depend(named, dependencies);
        }

        // Lifelines are not informed deleted unless the object is marked listed
        if (lifelines.length > 0) {
            this.commit(...lifelines);
        }

        return named;
    }

    /*
        Given an object it returns its dependencies direct or indirectly
        This set only contains objects that are fetched/new but not listed objects
    */
    public references<T extends Named>(named: T, direct: boolean = false): Set<Named> | undefined {
        if (ModelMarker.isListed(named)) {
            return Dependency.dependencies(named);
        }
        return direct
            ? Walker.directConceptDependencies(this._modelAttributeContext, named)
            : Walker.conceptDependencies(this._modelAttributeContext, this.propertyInspector, named);
    }

    /*
        Given an object it returns all the objects using it directly or indirectly.
        This set only contains objects that are fetched/new but not listed objects
    */
    public referencedBy<T extends Named>(named: T, direct: boolean = false): Set<Named> {
        const referenceBy = new Set<Named>();
        this.idToObjectMap.forEach((value) => {
            if (value !== named && !ModelMarker.isDeleted(value)) {
                if (!referenceBy.has(value)) {
                    if (
                        direct
                            ? Walker.directConceptDependencies(this._modelAttributeContext, value).has(named)
                            : Walker.conceptDependencies(
                                this._modelAttributeContext,
                                this.propertyInspector,
                                value
                            ).has(named)
                    ) {
                        referenceBy.add(value);
                    }
                }
            }
        });

        this.idToCommittedObjectMap.forEach((value) => {
            if (!ModelMarker.isDeleted(value)) {
                if (!referenceBy.has(value)) {
                    if (
                        direct
                            ? Walker.directConceptDependencies(this._modelAttributeContext, value).has(named)
                            : Walker.conceptDependencies(
                                this._modelAttributeContext,
                                this.propertyInspector,
                                value
                            ).has(named)
                    ) {
                        referenceBy.add(value);
                    }
                }
            }
        });
        return referenceBy;
    }

    public directReferencedBy<T extends Named>(named: T): Set<Named> {
        const startTime = performance.now();
        const conceptName = named ? Metamodel.modelOf(named).concept.name : '';
        const referenceBy = new Set<Named>();
        this.idToObjectMap.forEach((value) => {
            if (
                value !== named &&
                !ModelMarker.isDeleted(value) &&
                this._weightOfResourceType(value) >= this._weightOfResourceType(named)
            ) {
                if (
                    !referenceBy.has(value) &&
                    Walker.hasDirectConceptDependencies(this._modelAttributeContext, value, named)
                ) {
                    referenceBy.add(value);
                }
            }
        });
        this.idToCommittedObjectMap.forEach((value) => {
            if (
                !ModelMarker.isDeleted(value) &&
                this._weightOfResourceType(value) >= this._weightOfResourceType(named)
            ) {
                if (
                    !referenceBy.has(value) &&
                    Walker.hasDirectConceptDependencies(this._modelAttributeContext, value, named)
                ) {
                    referenceBy.add(value);
                }
            }
        });

        this.logger.logEvent(
            new PerformanceEvent(
                ModelService.className,
                loggerConstants.Operation_GetDirectReferencedBy,
                performance.now() - startTime,
                '1',
                conceptName
            )
        );
        return referenceBy;
    }

    /*
        Removes all the reference to this object from other objects and marks those
        objects to be broken. Deleted flag says only check for deleted objects. Leave the others
        alone
    */
    public unreference(named: Named, deleted?: boolean): Set<Named> {
        const unreferenced = new Set<Named>();

        const namedSet = new Set<Named>();
        if (!deleted || ModelMarker.isDeleted(named)) {
            namedSet.add(named);
        }

        const handleError = (e: Error, namedWithFailure: Named) => {
            if (!namedWithFailure.isOk) {
                // It's OK to fail on bad resource
                this.logger.warn(
                    'ModelService',
                    `Failed on unreference with bad resource ${namedWithFailure.name}. Error: ${convertObjectToString(
                        e
                    )}`
                );
            } else {
                this.logger.error(
                    'ModelService',
                    `Failed on unreference with resource ${namedWithFailure.name}. Error: ${convertObjectToString(e)}`
                );
                throw e;
            }
        };

        try {
            Walker.walk(
                this._modelAttributeContext,
                (_object, _property, value, attribute, _parameters) => {
                    if (!value) {
                        return Direction.SKIP;
                    }
                    if (
                        (attribute && (attribute.isReferenced(this._modelAttributeContext) || attribute.primitive)) ||
                        !Metamodel.isModelOf(value)
                    ) {
                        return Direction.SKIP;
                    }
                    const valueModel = Metamodel.modelOf(value);
                    if (
                        Named.MODEL.isAncestor(valueModel) &&
                        valueModel.globalScope &&
                        value.conceptOwner === named &&
                        (!deleted || ModelMarker.isDeleted(named))
                    ) {
                        namedSet.add(value);
                    }
                    return Direction.GO;
                },
                this.propertyInspector,
                named
            );
        } catch (e) {
            handleError(e, named);
        }

        if (namedSet.size === 0) {
            return unreferenced; // Nothing really was deleted here. Make a quick exit
        }

        const walk = (reference: Named) => {
            try {
                Walker.walk(
                    this._modelAttributeContext,
                    (object, property, value, attribute, parameters) => {
                        if (!value) {
                            return Direction.SKIP;
                        }
                        if ((attribute && attribute.primitive) || !Metamodel.isModelOf(value)) {
                            return Direction.SKIP;
                        }

                        const valueModel = Metamodel.modelOf(value);

                        // It should either be a concept or one with global scope like column
                        if (!valueModel.concept || !valueModel.globalScope) {
                            return Direction.GO;
                        }
                        const namedValue = value as Named;
                        // This named value has not presence in the one deleted.
                        // Closure on all contained values in named that got deleted
                        if (!namedSet.has(namedValue)) {
                            // If it is a reference concept not the same skip it...else process
                            return attribute?.isReferenced(this._modelAttributeContext) ? Direction.SKIP : Direction.GO;
                        }

                        unreferenced.add(reference);
                        markBroken(reference);

                        const thisValue = object[property];
                        if (Array.isArray(thisValue)) {
                            // Remove the old entry and replace with empty
                            const array = thisValue as Named[];
                            array.splice(parameters[0], 1);
                        } else if (isSet(thisValue)) {
                            const aSet = thisValue as Set<Named>;
                            aSet.delete(namedValue);
                        } else if (isMap(thisValue)) {
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const aMap = thisValue as Map<any, any>;
                            aMap.delete(parameters[0]); // Key or value, the entry goes
                        } else {
                            delete object[property]; // Rewire it even if it is listed
                        }

                        return Direction.GO;
                    },
                    this.propertyInspector,
                    reference
                );
            } catch (e) {
                handleError(e, reference);
            }
        };

        for (const reference of this.idToObjectMap.values()) {
            if (reference !== named) {
                walk(reference);
            }
        }

        // Uncommitted objects can also reference this object
        for (const reference of this.idToCommittedObjectMap.values()) {
            if (reference !== named) {
                walk(reference);
            }
        }

        return unreferenced;
    }

    /**
     * Returns all entities that directly referred to the named entity
     */
    public relatedEntities(named: Named): Promise<Named[]> {
        return new Promise((resolve, reject) => {
            // time-consuming function, have to put in task queue
            setTimeout(() => {
                const references = this.directReferencedBy(named);
                const result = Array.from(references);
                resolve(result);
            }, 0);
        });
    }

    public async save<T extends Named>(...nameds: T[]): Promise<ISaveResult> {
        return this.saveWithValidation(undefined, ...nameds);
    }

    public async saveWithValidation<T extends Named>(context: IMutationContext, ...nameds: T[]): Promise<ISaveResult> {
        const violations: IOwnedViolation[] = [];
        nameds.forEach((resource) => {
            const model = Metamodel.modelOf(resource);
            const gitValidator = model.tag('gitValidator');
            if (gitValidator && gitValidator instanceof Function) {
                violations.push(...gitValidator(resource));
            }
        });
        if (violations.length) {
            this.logger.log(ModelService.className, 'saveWithValidation - validation failed.', {
                violations: violations.map((v) => v.customDisplayName).join(', ')
            });
            return {
                status: SaveStatus.Failed,
                error: stringResources.SaveAbortValidationFailed,
                violations
            };
        }
        try {
            const result = await this.saveAs(context, ...nameds);
            if (!result || result.status !== SaveStatus.Succeeded) {
                this.logger.warn(ModelService.className, 'saveWithValidation - unsuccessful.', {
                    status: String(result && result.status),
                    error: result && result.error,
                    violations:
                        result && result.violations && result.violations.map((v) => v.customDisplayName).join(', ')
                });
            } else {
                this.logger.log(ModelService.className, 'saveWithValidation - succeeded.');
            }
            return result;
        } catch (err) {
            this.logger.error(
                ModelService.className,
                `saveWithValidation - error thrown. Error: ${convertObjectToString(err)}`
            );
            throw err;
        }
    }

    /*
        Saves a set of nameds
    */
    public async saveAs<T extends Named>(context?: IMutationContext, ...nameds: T[]): Promise<ISaveResult> {
        if (!nameds || !nameds.length) {
            // Nothing to save.
            this.logger.log(ModelService.className, `saveAs - There is nothing to save, nameds is empty.`);
            return { status: SaveStatus.Succeeded };
        }
        const countByArtifactType: Record<string, number> = {};
        try {
            // Log count of each kind of artifact
            nameds.forEach((named) => {
                const conceptName = named ? Metamodel.modelOf(named).concept.name : '';
                countByArtifactType[conceptName] = (countByArtifactType[conceptName] || 0) + 1;
            });
        } catch (e) {
            /* Dismiss */
        }
        this.logger.log(ModelService.className, `saveAs - Starting to save ${nameds.length} nameds.`, {
            count: JSON.stringify(countByArtifactType)
        });

        const namedSet = new Set<Named>();
        // eslint-disable-next-line prefer-object-spread
        context = Object.assign({}, context);

        if (context === undefined) {
            throw new Error(' Unreachable code'); // TS error
        }
        if (!context.adapterContext) {
            context.adapterContext = this.saveAdapterContext; // Overwrite
        }
        context.adapterContext.writeIdentities = this._resourceFilesProvider.writeIdentities;
        for (const named of nameds) {
            const model = Metamodel.modelOf(named);
            if (__IS_FABRIC_MDF__ || this._isDedicatedIFrame) {
                if (
                    !(await this.iframeMessengerService
                        .saveResourceInDedicatedIframeAsync(
                            await ModelUtilties.toResource(AdapterContext.defaultContextOf(this._contextData), named),
                            model.name,
                            null
                        )
                        .catch((error) => {
                            throw new Error(error);
                        }))
                ) {
                    throw new Error('Resource could not be saved');
                }
            }
            if (!named.name) {
                const id = named.owner && (named.owner as Named).name ? (named.owner as Named).name : '';
                this.logger.error(model.name, `saveAs - Only named objects can be saved - ${id}`);
                throw new Error(`Only named objects can be saved - ${id}`);
            }
            if (
                !ModelMarker.isDeleted(named) &&
                !this.saveAdapterContext.adapterFor(model) &&
                !this.saveAdapterContext.asyncAdapterFor(model)
            ) {
                this.logger.error(model.name, `saveAs - No adapter registered for model ${model.name}`);
                throw new Error(`No adapter registered for model ${model.name}`);
            }
            namedSet.add(named);
            // This trancends committed and saved lists
            if (!named.isOk && ModelMarker.isDeleted(named)) {
                // on delete bad resource, skip checking dependencies
                continue;
            }
            Walker.conceptDependencies(this._modelAttributeContext, this.propertyInspector, named).forEach(
                (conceptNamed) => namedSet.add(conceptNamed)
            );
        }
        this.identify(namedSet);
        await this.fillReferencedByMap(namedSet);

        const mutations: IMutation[] = [];

        for (const named of namedSet) {
            if (!named.name) {
                // TS is not smart to figure out that this was already checked
                continue;
            }
            const model = Metamodel.modelOf(named);

            const concept = model.concept;
            if (!concept) {
                this.logger.error(model.name, `saveAs - Cannot save an object of type ${model.name}`);
                throw new Error(`Cannot save an object of type ${model.name}`);
            }

            if (ModelMarker.isDeleted(named)) {
                mutations.push({
                    mutation: model.transient || ModelMarker.isNew(named) ? Mutation.NOOP : Mutation.DELETE,
                    named,
                    then: () => {
                        if (!this.dataProviderService.isGitMode()) {
                            const originalShadow = Shadow.shadow(named);
                            // we only log the deleted nameds equipped with a shadow named, which infers that it's an existing model, not via new + delete
                            // SqlPool is managed by extension, we don't take it into consideration when reverting changes
                            if (originalShadow && !(originalShadow instanceof SqlPool)) {
                                this._deletedNameds.add(named);
                            }
                        }
                        this.idToObjectMap.delete(named.id);
                        this.identityToObjectMap.delete(this._identityService.identity(named));
                        this._deletedResource$.next({
                            resourceType: named instanceof A365Named ? named.resourceType : model.name,
                            name: named.name,
                            folderName: named instanceof A365Named ? named.folder.name : null
                        });
                        this.subject.next({ index: this._nextSubjectIndex(), subject: named, added: false });
                        markZombie(named);
                        this.unreference(named);
                        // Either in the new/delete case remove the entries
                        this.idToCommittedObjectMap.delete(named.id); // Remove if it exists.
                        this.identityToCommittedObjectMap.delete(this._identityService.identity(named));
                    }
                });
            } else if (ModelMarker.isNew(named) || ModelMarker.isDirty(named)) {
                // Find if this is a possible rename. We have to do a rename followed by a update
                // New objects do not have a shadow because they can't go back to anything
                const shadow = Shadow.shadow(named);
                if (ModelMarker.isFetched(named) && shadow && shadow.name !== named.name) {
                    // Case of rename
                    mutations.push({
                        mutation: model.transient ? Mutation.NOOP : Mutation.RENAME,
                        named,
                        then: () => {
                            Shadow.cast(this._modelAttributeContext, named);
                        }
                    });
                }

                // We can't figure out update vs rename. We just update the renamed one anyway
                mutations.push({
                    mutation: Constants.GlobalVariables.isAutosaveSupportType(concept.name as Constants.ResourceType)
                        ? model.transient
                            ? Mutation.NOOP
                            : ModelMarker.isNewInPublish(named)
                                ? Mutation.ADD
                                : Mutation.UPDATE
                        : model.transient
                            ? Mutation.NOOP
                            : ModelMarker.isNew(named)
                                ? Mutation.ADD
                                : Mutation.UPDATE,
                    named,
                    toResources: ModelUtilties.toResources,
                    then: async () => {
                        this.idToObjectMap.set(named.id, named);
                        this.identityToObjectMap.set(this._identityService.identity(named), named);
                        if (ModelMarker.isNew(named) && !this.idToCommittedObjectMap.has(named.id)) {
                            // We would not already added this
                            this.subject.next({ index: this._nextSubjectIndex(), subject: named, added: true });
                        }
                        this._modelMarkerWrapper.markFetched(named);
                        // Either in the new/delete case remove the entries
                        this.idToCommittedObjectMap.delete(named.id); // Remove if it exists.
                        this.identityToCommittedObjectMap.delete(this._identityService.identity(named));
                        this.unreference(named, true);
                        if (named instanceof DataFlow) {
                            const asyncAdapter = this.saveAdapterContext.asyncAdapterFor(model);
                            const resource: IDataFlow = await asyncAdapter.toResource(named);
                            named.setLastStoredResource(resource);
                        }
                        Shadow.cast(this._modelAttributeContext, named);
                    }
                });
            }
        }

        const mutator: IMutator = new ResourceMutator(this._contextData, this._resourceFilesProvider, this.logger);
        return await mutator.mutate(mutations, context);
    }

    /*
        Commit a set of nameds
    */
    public commit<T extends Named>(...nameds: T[]): boolean {
        const namedSet = new Set<Named>();
        this.logger.log(ModelService.className, `commit - Added ${nameds.length} nameds for commit.`);
        for (const named of nameds) {
            const concept = Metamodel.modelOf(named).concept;
            const conceptName = concept ? concept.name : '';
            if (
                !(
                    ModelMarker.isNew(named) ||
                    (Constants.GlobalVariables.isAutosaveSupportType(conceptName as Constants.ResourceType) &&
                        ModelMarker.isNewInPublish(named))
                )
            ) {
                this.logger.error(Metamodel.modelOf(named).name, 'Only new objects can be committed');
                throw new Error('Only new objects can be committed');
            }
            // Will include all the lifeline objects along with this. The reference may be a combination of new & fetched
            // Find everyone who has me as their lifeline. The lifeline's lifeline should also be recursively handled
            if (ModelMarker.isDeleted(named)) {
                Walker.directConceptDependencies(this._modelAttributeContext, named).forEach((value) =>
                    ModelMarker.lifeline(value) === named ? (nameds as Named[]).push(value) : undefined
                );
            } else if (!named.name) {
                const id = named.owner && (named.owner as Named).name ? (named.owner as Named).name : '';
                this.logger.error(Metamodel.modelOf(named).name, `Only named objects can be committed - ${id}`);
                throw new Error(`Only named objects can be committed - ${id}`);
            }
            namedSet.add(named);
        }

        for (const named of namedSet) {
            if (ModelMarker.isDeleted(named)) {
                markZombie(named);
                this.idToCommittedObjectMap.delete(named.id);
                this.identityToCommittedObjectMap.delete(this._identityService.identity(named));
                this.subject.next({ index: this._nextSubjectIndex(), subject: named, added: false });
            } else if (!this.idToCommittedObjectMap.has(named.id)) {
                if (!named.name) {
                    continue;
                }
                this.idToCommittedObjectMap.set(named.id, named);
                this.identityToCommittedObjectMap.set(this._identityService.identity(named), named);
                this.subject.next({ index: this._nextSubjectIndex(), subject: named, added: true });
            }
        }
        return true;
    }

    /*
        Returns a unique name for a model so it does not conflict with existing object names
        Only reusable objects are supported(for e.g. pipeline, linkedservice etc).
        For non-reusable objects(like activities), the container should provide specific methods.
    */
    public uniqueName(model: IModel, prefix?: string, notAppendNumberWhenNameIsUnique?: boolean): string {
        // We assume names are case-insensitive. It is essentially for a concept class of the object
        if (!model.concept) {
            // You are in the wrong place for naming
            this.logger.error(model.name, 'Only concept object types allowed');
            throw new Error('Only concept object types allowed');
        }

        const concept = model.concept;
        if (!prefix) {
            prefix = namingPrefix.get(model.name) || model.name;
        }

        const prefixLowerCase = prefix.toLowerCase();
        const regex = new RegExp('^' + prefixLowerCase + '[0-9]+$');
        let maxNumber = 0;
        let nameIsUnique = true;
        const findMaxNumber = (named: Named) => {
            if (Metamodel.modelOf(named).concept === concept && named.name) {
                if (named.name.toLowerCase().match(regex)) {
                    // eslint-disable-next-line radix
                    const thisSuffix = Number.parseInt(named.name.slice(prefix.length));
                    if (thisSuffix > maxNumber) {
                        maxNumber = thisSuffix;
                    }
                }
                if (notAppendNumberWhenNameIsUnique && named.name.toLowerCase() === prefixLowerCase) {
                    nameIsUnique = false;
                }
            }
        };
        for (const named of this.idToObjectMap.values()) {
            findMaxNumber(named);
        }

        for (const named of this.idToCommittedObjectMap.values()) {
            findMaxNumber(named);
        }

        if (notAppendNumberWhenNameIsUnique && nameIsUnique) {
            return prefix;
        }
        return prefix.concat((maxNumber + 1).toString());
    }

    public uniqueNameWithRandomNum(
        model: IModel,
        prefix?: string,
        notAppendNumberWhenNameIsUnique?: boolean,
        max: number = 999
    ): string {
        // Generate random number between 1 and max
        const num = Math.floor(Math.random() * max) + 1;
        return this.uniqueName(model, prefix, notAppendNumberWhenNameIsUnique).concat(num.toString());
    }

    /*
        The unique name checks should be proactively performed but there are also cases
        where an object may be closed unsaved and the list of unique names will differ
        If a named is passed it is assumed that is the object to be renamed
    */
    public isUniqueName(name: string, model: IModel, named?: Named): boolean {
        if (name == null) {
            return false;
        }
        if (__IS_FABRIC_DPE__ && named && named instanceof Pipeline && named.workspaceObjectId && named.workspaceObjectId !== this._contextData.contextId) {
            return true;
        }
        const concept = model.concept;

        for (const anamed of this.idToObjectMap.values()) {
            if (__IS_FABRIC_DPE__ && anamed instanceof Pipeline && anamed.workspaceObjectId && anamed.workspaceObjectId !== this._contextData.contextId) {
                continue;
            }
            if (
                Metamodel.modelOf(anamed).concept === concept &&
                ((named && this._identityService.identity(named) !== this._identityService.identity(anamed)) ||
                    !named) &&
                anamed.name &&
                anamed.name.toUpperCase() === name.toUpperCase()
            ) {
                return false;
            }
        }

        for (const anamed of this.idToCommittedObjectMap.values()) {
            if (
                Metamodel.modelOf(anamed).concept === concept &&
                ((named && this._identityService.identity(named) !== this._identityService.identity(anamed)) ||
                    !named) &&
                anamed.name &&
                anamed.name.toUpperCase() === name.toUpperCase()
            ) {
                return false;
            }
        }
        return true;
    }

    public revertToNew(named: Named): void {
        this.idToCommittedObjectMap.set(named.id, named);
        this.identityToCommittedObjectMap.set(this._identityService.identity(named), named);
        this.idToObjectMap.delete(named.id);
        this.identityToObjectMap.delete(this._identityService.identity(named));
    }

    public replaceNamed(replaceThis: Named, withThis: Named): void {
        // remove the one to be replaced.
        if (this.idToObjectMap.has(replaceThis.id)) {
            this.idToObjectMap.delete(replaceThis.id);
            this.identityToObjectMap.delete(this._identityService.identity(replaceThis));
            this.idToObjectMap.set(withThis.id, withThis);
            this.identityToObjectMap.set(this._identityService.identity(withThis), withThis);
            this.subject.next({ index: this._nextSubjectIndex(), subject: withThis, added: true });
            this.subject.next({ index: this._nextSubjectIndex(), subject: replaceThis, added: false });
        } else if (this.idToCommittedObjectMap.has(replaceThis.id)) {
            this.idToCommittedObjectMap.delete(replaceThis.id);
            this.identityToCommittedObjectMap.delete(this._identityService.identity(replaceThis));
            this.idToCommittedObjectMap.set(withThis.id, withThis);
            this.identityToCommittedObjectMap.set(this._identityService.identity(withThis), withThis);
            this.subject.next({ index: this._nextSubjectIndex(), subject: withThis, added: true });
            this.subject.next({ index: this._nextSubjectIndex(), subject: replaceThis, added: false });
        }
    }

    public updateObjectMap(oldIdentifier: string, named: Named): void {
        if (ModelMarker.isNew(named)) {
            this.identityToCommittedObjectMap.delete(oldIdentifier);
            this.idToCommittedObjectMap.set(named.id, named);
            this.identityToCommittedObjectMap.set(this._identityService.identity(named), named);
        } else {
            this.identityToObjectMap.delete(oldIdentifier);
            this.idToObjectMap.set(named.id, named);
            this.identityToObjectMap.set(this._identityService.identity(named), named);
        }
    }

    public addToObjectMap(named: Named): void {
        if (ModelMarker.isNew(named)) {
            this.idToCommittedObjectMap.set(named.id, named);
            this.identityToCommittedObjectMap.set(this._identityService.identity(named), named);
        } else {
            this.idToObjectMap.set(named.id, named);
            this.identityToObjectMap.set(this._identityService.identity(named), named);
        }
    }

    public revertDeletedNameds(resourceType?: Constants.ResourceType, parentInfo?: IParentResource): void {
        const revertedNameds = [...this._deletedNameds].filter((named) => {
            if (!resourceType && !parentInfo) {
                return true;
            }
            const resourceBase = ResourceFilesUtils.namedToResourceBase(named);
            return (
                resourceType &&
                resourceBase.type === resourceType &&
                (!parentInfo || isEqual(resourceBase.parentResource, parentInfo))
            );
        });

        for (const named of revertedNameds) {
            // reflect shadow to named in case any changes on named
            Shadow.reflect(this._modelAttributeContext, named);
            this._modelMarkerWrapper.markFetched(named); // change these guys state back to fetch
            this.idToObjectMap.set(named.id, named);
            this.identityToObjectMap.set(this._identityService.identity(named), named);
            this.subject.next({ index: this._nextSubjectIndex(), subject: named, added: true });
            this.cleanupDeletedNamedsCache(named);
        }
    }

    public cleanupDeletedNamedsCache(resourceType?: Constants.ResourceType, parentInfo?: IParentResource): void;
    public cleanupDeletedNamedsCache(named?: Named): void;
    public cleanupDeletedNamedsCache(arg1?: Named | Constants.ResourceType, arg2?: IParentResource): void {
        if (arg1 instanceof Named) {
            this._deletedNameds.delete(arg1);
        } else if (arg1) {
            const cleanupNameds = [...this._deletedNameds].filter((named) => {
                const resourceBase = ResourceFilesUtils.namedToResourceBase(named);
                return arg1 && resourceBase.type === arg1 && (!arg2 || isEqual(resourceBase.parentResource, arg2));
            });
            cleanupNameds.forEach((named) => {
                this.cleanupDeletedNamedsCache(named);
            });
        } else {
            this._deletedNameds.clear();
        }
    }

    /*
        Goal of this method is to resync the changes
    */
    public async resynchronize(): Promise<void> {
        this.logger.log(ModelService.className, `resynchronize - start`);
        const startTime = performance.now();
        this._stopFetchAllInBackground();
        // Remove all the previous nameds
        this.idToObjectMap.forEach((value, name) => {
            setTimeout(() => {
                this.subject.next({ index: this._nextSubjectIndex(), subject: value, added: false });
            }, 0);
        });
        this.idToObjectMap.clear();
        this.identityToObjectMap.clear();

        this.logger.log(ModelService.className, 'resynchronize - start loading resources');
        this.isLoading.next(true);
        this._isFetching.next(false);

        await this.loadDynamicConnectors();

        if (this._resourceSubscription) {
            this._resourceSubscription.unsubscribe();
            this._resourceSubscription = undefined;
            this.logger.log(ModelService.className, 'resynchronize - unsubscribe _resourceSubscription');
        }
        this._resourceFilesProvider.reset();
        this._resourceSubscription = this._resourceFilesProvider.resourceSubject.subscribe({
            next: async (resources) => await this.synchronizeInternal(resources),
            error: (err) =>
                this.logger.error(
                    ModelService.className,
                    `resynchronize - error on resourceSubscription, Error: ${convertObjectToString(err)}`
                )
        });

        if (await this._resourceFilesProvider.reinitialize(false)) {
            this.logger.logEvent(
                new PerformanceEvent(
                    ModelService.className,
                    loggerConstants.Operation_ResourceReload,
                    performance.now() - startTime,
                    this.identityToObjectMap.size.toString()
                )
            );

            this.fetchAllInBackground();
            this.isLoading.next(false);
            this.logger.log(ModelService.className, 'resynchronize - finished');
        }
    }

    /**
     * Re-populate resources based on type
     * Main flow, 1. re-download backend resources, 2, re-fetch existing nameds except those in excludedNameds, 3. delete and unreference nameds when necessary
     * Limitation: When named.objectId is null, decluttering nameds is simply based on name, causing we cannot handle rename scenario well since there's no unique key telling which resource maps to which named
     */
    public async resynchronizeResource(
        baseType: Constants.ResourceType,
        parentResourceType?: Constants.ResourceType,
        parentUniqueKey?: string,
        excludedNameds?: Named[]
    ): Promise<void> {
        this._refreshStatusMap.set(baseType, true);
        const resourceType =
            parentResourceType && parentUniqueKey ? `${parentResourceType}s/${parentUniqueKey}/${baseType}` : baseType;

        this.logger.log(ModelService.className, `resynchronizeResource ${resourceType} - start`);
        const startTime = performance.now();

        this.logger.log(ModelService.className, `resynchronizeResource ${resourceType} - start loading resources`);

        if (this._resourceSubscription) {
            this._resourceSubscription.unsubscribe();
            this._resourceSubscription = undefined;
        }
        this._resourceFilesProvider.resetResource(baseType, parentResourceType, parentUniqueKey); // only reset resource for specified resource

        this._resourceSubscription = this._resourceFilesProvider.resourceSubject.subscribe({
            next: async (resources) => await this.synchronizeInternal(resources), // handle the new fetched resources
            error: (err) =>
                this.logger.error(
                    ModelService.className,
                    `resynchronizeResource ${resourceType} - error on resourceSubscription, Error: ${convertObjectToString(
                        err
                    )}`
                )
        });

        if (
            await this._resourceFilesProvider.reinitializeResource(false, baseType, parentResourceType, parentUniqueKey)
        ) {
            this.logger.logEvent(
                new PerformanceEvent(
                    ModelService.className,
                    loggerConstants.Operation_ResourceReload,
                    performance.now() - startTime,
                    this.identityToObjectMap.size.toString()
                )
            );

            await this.fetchInSequence(baseType, parentResourceType, parentUniqueKey, true, excludedNameds);

            // resources are handled well, let's de-clutter nameds
            const nameds: Named[] = await this._getNamedListPerType(
                baseType,
                parentResourceType,
                parentUniqueKey,
                excludedNameds
            );

            const refreshedResources = await this._resourceFilesProvider.listParentAndChildResources(
                baseType,
                parentResourceType,
                parentUniqueKey
            );
            const refreshedObjectIds = refreshedResources.map((item) => item.objectId).filter((item) => !!item);
            const refreshedObjectNames = refreshedResources.map((item) => item.name).filter((item) => !!item);
            const refreshedResourceIds = refreshedResources
                .map((item) => this._identityService.generateIdentifierFromResource(item))
                .filter((item) => !!item);

            // objectId is a unique key for us to link nameds and resources
            const shouldDeleteByObjectId = nameds.filter((named: Named) =>
                named.objectId
                    ? // deleted
                    refreshedObjectIds.indexOf(named.objectId) === -1 ||
                    // renamed
                    refreshedObjectNames.indexOf(named.name) === -1
                    : false
            );
            // when no objectId is found, lets fallback to id to perform best-effort de-cluttering
            const shouldDeleteByResourceId = nameds.filter((named: Named) =>
                !named.objectId && named.id
                    ? (!parentUniqueKey ||
                        !parentResourceType ||
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        ((named as any).parentInfo &&
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            (named as any).parentInfo.resourceName &&
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            (named as any).parentInfo.resourceName.toLowerCase() ===
                            parentUniqueKey.toLowerCase())) &&
                    refreshedResourceIds.indexOf(this._identityService.identity(named)) === -1
                    : false
            );
            const shouldDelete = [...shouldDeleteByObjectId, ...shouldDeleteByResourceId];

            shouldDelete.forEach((named) => {
                this.idToObjectMap.delete(named.id);
                this.identityToObjectMap.delete(this._identityService.identity(named));
                this.unreference(named, true);
                this.subject.next({ index: this._nextSubjectIndex(), subject: named, added: false });
            });

            // batch update nameds
            nameds.forEach((named: Named) => {
                if (shouldDelete.indexOf(named) === -1) {
                    this.subject.next({ index: this._nextSubjectIndex(), subject: named, added: false });
                    this.subject.next({ index: this._nextSubjectIndex(), subject: named, added: true });
                }
            });

            this.logger.log(ModelService.className, `resynchronizeResource ${resourceType} - finished`);
        }
        this._refreshStatusMap.set(baseType, false);
    }

    public async importNamedFromResources(resources: IFullResource[]): Promise<void> {
        return this._createAndFetchEntities(resources, false, 'importNamedFromResources', true);
    }

    public getRefreshStatus(baseType: Constants.ResourceType): boolean {
        return this._refreshStatusMap.get(baseType);
    }

    /**
     * weightOfResourceTypeCore: Concept with higher weight can reference those with lower weight
     */
    public weightOfResourceTypeCore(concept: string): number {
        switch (concept) {
            case 'factory':
                return 0;
            case 'managedvirtualnetwork':
                return 1;
            case 'integrationruntime':
                return 2;
            case 'credential':
                return 3;
            case 'linkedservice':
                return 4;
            case 'sparkconfiguration':
            case 'mangedprivateendpoint':
                return 5;
            case 'sqlpool':
                return 6;
            case 'bigdatapool':
                return 7;
            case 'udflibrary':
            case 'dataset':
                return 8;
            case 'flowlet':
                return 9;
            case 'pbidataflow':
            case 'dataflow':
            case 'powerquery':
                return 10;
            case 'linkconnection':
            case 'sparkjobdefinition':
            case 'scopejobdefinition':
            case 'notebook':
                return 11;
            case 'pipeline':
                return 12;
            case 'trigger':
                return 13;
            default:
                return 14;
        }
    }

    /**
     * Get named by objectId.
     * @param objectId object id
     * @param resourceType resource type
     * @returns named entity
     */
    public async getNamedByObjectId<T extends Named>(
        objectId: string,
        resourceType: Constants.ResourceType
    ): Promise<T> {
        return (
            (await lastValueFrom(
                this.list(undefined, Metamodel.modelByName(resourceType)).pipe(
                    take(1),
                    timeout(3000),
                    catchError(() => of([]))
                )
            )) as T[]
        ).find((entity) => entity.objectId === objectId);
    }

    /**
     * _weightOfResourceType: Concept with higher weight can reference those with lower weight
     */
    private _weightOfResourceType(named: Named): number {
        const concept = Metamodel.modelOf(named).concept.name.toLowerCase();
        if (concept === 'dataflow') {
            return this.weightOfResourceTypeCore(Metamodel.modelOf(named).name.toLowerCase());
        }
        return this.weightOfResourceTypeCore(concept);
    }

    private _excludeOndemandFetchedNameds<T>(nameds: T[]): T[] {
        const resourceTypesToSkipFetch: string[] = this._getResourceTypesToSkipFetch();
        return nameds.filter((item) => {
            const concept = Metamodel.modelOf(item).concept;
            if (concept && resourceTypesToSkipFetch.includes(concept.name)) {
                return false;
            }
            return true;
        });
    }

    private _getResourceTypesToSkipFetch(): string[] {
        const resourceTypesToSkipFetch = [];
        if (__IS_FABRIC_DPE__) {
            resourceTypesToSkipFetch.push(ModelConstants.pipeline);
        }
        return resourceTypesToSkipFetch;
    }

    /*
      Synchronize is called at init time.
      TODO: We need to enhance this with merge code
    */
    private async synchronize(): Promise<void> {
        this.logger.log(ModelService.className, `synchronize - start`);
        const startTime = performance.now();

        this.isLoading.next(true);

        await this.loadDynamicConnectors();

        if (this._resourceSubscription) {
            this._resourceSubscription.unsubscribe();
            this._resourceSubscription = undefined;
        }
        // Telemetry on first chunk resources downloaded, timeout with 5min
        this._resourceFilesProvider.resourceSubject
            .pipe(filter((resources) => resources.length > 0))
            .pipe(take(1), timeout({ each: 300000, with: () => from([]) }))
            .subscribe((resources) => {
                this.logger.logEvent(
                    new PerformanceEvent(
                        ModelService.className,
                        loggerConstants.Operation_FirstSetResourceDownload,
                        performance.now() - startTime,
                        resources.length
                    )
                );
            });
        this._resourceSubscription = this._resourceFilesProvider.resourceSubject.subscribe({
            next: async (resources) => await this.synchronizeInternal(resources),
            error: (err) =>
                this.logger.error(ModelService.className, `synchronize - Error: ${convertObjectToString(err)}`)
        });
        // in Trident, we resynchronize to list resource
        // MNICK TO DO CHECK THIS
        this._resourceFilesProvider.init(false, __IS_FABRIC_DPE__ || __IS_FABRIC_ADF__).then(() => {
            this.logger.logEvent(
                new PerformanceEvent(
                    ModelService.className,
                    loggerConstants.Operation_ResourceDownload,
                    performance.now() - startTime,
                    this.identityToObjectMap.size.toString()
                )
            );

            this.fetchAllInBackground();
            this.isLoading.next(false);
            this.logger.log(ModelService.className, `synchronize - finished`);
        });
    }

    /**
     * Fetch each named in background. This is used after all nameds are listed.
     * After this is complete, scenarios require all nameds fetched (rename/validateAll/publish, etc.) should not have perf issue caused by fetch
     */
    private async fetchAllInBackground(): Promise<void> {
        if (Constants.GlobalVariables.enableDebugMode) {
            // Do not fetch nameds in background when enableDebugMode is true
            return;
        }

        this.logger.log(ModelService.className, 'fetchAllInBackground - start');
        let nameds: Named[] = await lastValueFrom(this.list().pipe(timeout({ each: 5, with: () => from<Named[][]>([]) })), {
            defaultValue: []
        });

        // Handle the only scenario where factory can be undefined, the select factory page.
        if (Constants.GlobalVariables.enableGlobalParameters && this._urlInformationService.resourceName) {
            const factories = await lastValueFrom(
                this.list(new RegExp(this._urlInformationService.resourceName, 'i'), Factory.MODEL).pipe(
                    timeout({ each: 5, with: () => from([]) })
                ),
                { defaultValue: [] }
            );
            let factory: Factory;
            if (factories && factories[0]) {
                factory = factories[0];
                factory.location = factory.location || this._urlInformationService.resourceLocation;
            } else {
                factory = new Factory(
                    this._urlInformationService.resourceName,
                    this._urlInformationService.resourceLocation
                );
                this._modelMarkerWrapper.markNew(factory);
            }
            nameds.push(factory);
        }

        // Still we need to restore autosaved content. C.f. `this.fetchOne`.
        if (__IS_A365__) {
            const notebooks = nameds.filter((n) => {
                const concept = Metamodel.modelOf(n).concept;
                const conceptName = concept ? concept.name : '';
                return conceptName.toLowerCase() !== 'notebook';
            });
            for (const named of notebooks) {
                const concept = Metamodel.modelOf(named).concept;
                const states = await this._resourceFilesProvider.getResourceStateBySource(
                    this._identityService.identity(named),
                    concept.name as Constants.ResourceType
                );
                if (states.includes(eModelState.SAVED)) {
                    // Ensure artifact is fetched. Also, this will update named as FETCHED (opt. DIRTY).
                    await this.fetch(named);
                }
            }
            // Notebooks are loaded on-demand. Not fetching here.
            nameds = nameds.filter((n) => Metamodel.modelOf(n).concept.name.toLowerCase() !== 'notebook');
        }

        const namedsToFetch = this._excludeOndemandFetchedNameds(nameds).sort(
            (a, b) => this._weightOfResourceType(a) - this._weightOfResourceType(b)
        );

        this._backgroundFetch(namedsToFetch, 0, performance.now());
    }

    /**
     * Add resource content in nameds
     */
    private async fetchInSequence(
        baseType: Constants.ResourceType,
        parentType?: Constants.ResourceType,
        parentUniqueKey?: string,
        force?: boolean,
        excludedNameds?: Named[]
    ): Promise<void> {
        if (Constants.GlobalVariables.enableDebugMode) {
            // Do not fetch nameds in background when enableDebugMode is true
            return;
        }

        this.logger.log(ModelService.className, 'fetchInSequence - start');
        const model = Metamodel.modelByName(baseType);
        const nameds: Named[] = await lastValueFrom(
            this.list(undefined, model).pipe(timeout({ each: 5, with: () => from([]) })),
            { defaultValue: [] }
        );

        let filteredNameds = nameds.filter((item) => !excludedNameds || excludedNameds.indexOf(item) === -1);
        if (parentType && parentUniqueKey) {
            filteredNameds = nameds.filter((named: Named) => {
                // For basic artifacts, the parentInfo is always null while for table and relationship, only new fetched nameds whose parentInfo would be null
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                return (named as DatabaseEntity).parentInfo == null;
            });
        }

        // Fetches the details of the named if not already fetched.
        const fetchPromises = filteredNameds.map(async (named: Named): Promise<void> => {
            await sleep(fetchDelayMs);
            const namedFetchStartTime = performance.now();
            await this.fetch(named, force);
            this._logPerfAlterIfNeeded(named, namedFetchStartTime, performance.now(), 'fetchInSequence');
        });
        await Promise.all(fetchPromises);

        this.logger.log(
            ModelService.className,
            `fetchInSequence - finished, duration: ${performance.now()}, total nameds: ${filteredNameds.length}`
        );
    }

    /**
     * Fetch nameds in background in sequence. fetchDelayMs is guaranteed between two nameds fetch (could be longer depends on execution queue)
     */
    private _backgroundFetch(nameds: Named[], index: number, initTime: number): void {
        this._backgroundFetchTimerHandlers.push(
            setTimeout(async () => {
                if (index < nameds.length) {
                    this._isFetching.next(Metamodel.modelOf(nameds[index]).concept.name);
                    const namedFetchStartTime = performance.now();
                    await this.fetch(nameds[index]);
                    if (nameds[index] instanceof Factory) {
                        this._factory = nameds[index] as Factory;
                    }
                    this._logPerfAlterIfNeeded(
                        nameds[index],
                        namedFetchStartTime,
                        performance.now(),
                        '_backgroundFetch'
                    );
                    this._backgroundFetch(nameds, index + 1, initTime);
                } else {
                    this._isFetching.next(0);
                    this.logger.log(
                        ModelService.className,
                        '_backgroundFetch - finished',
                        { duration: performance.now() - initTime, namedTotal: nameds.length }
                    );
                    const allUnknownTypes = Metamodel.models()
                        .filter((item) => item?.createdFromUnknownType)
                        .map((item) => item.name)
                        .join(',');
                    this.logger.log(ModelService.className, `_backgroundFetch - finished, total unknown model types`, {
                        allUnknownTypes
                    });
                }
            }, fetchDelayMs)
        );
    }

    /**
     * Log perf alert for fetching when the cost time is too large
     */
    private _logPerfAlterIfNeeded(named: Named, startTime: number, endTime: number, functionName: string): void {
        if (endTime - startTime > 5000) {
            // Log when fetch named takes longer than 5s
            this.logger.warn(
                ModelService.className,
                `${functionName} - Perf alert: take more than 5s to fetch resource`,
                {
                    ResourceName: named.name,
                    ResourceType: Metamodel.modelOf(named).concept.name,
                    TimeTakenInMs: (endTime - startTime).toString()
                }
            );
        }
    }

    private _stopFetchAllInBackground(): void {
        this._backgroundFetchTimerHandlers.forEach((handler: NodeJS.Timeout) => {
            clearTimeout(handler);
        });
        this._backgroundFetchTimerHandlers = [];
        this._isFetching.next(false);
        this.logger.log(ModelService.className, '_stopFetchAllInBackground - finished');
    }

    /**
     * Fills the referencedByMap. This is used during validation to get the resources
     * which are referencing the resource being edited/deleted.
     * @param namedSet: set of modified nameds
     */
    private async fillReferencedByMap(namedSet?: Set<Named>): Promise<void> {
        // In order to find the dependent we need the nameds to be fetched.
        // fetch is going to override the state, cache the state so that it can
        // be restored later.
        const existingStateMap: Map<string, number> = new Map();
        if (namedSet) {
            namedSet.forEach((named) => {
                if (named && named.extension && named.extension._state$) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    existingStateMap.set(this._identityService.identity(named), (named.extension._state$ as any).state);
                }
            });
        }
        const nameds = (await lastValueFrom(
            this.list().pipe(
                timeout({
                    each: 5,
                    with: () => from([])
                })
            ),
            { defaultValue: [] }
        )) as Named[];
        if (!nameds) {
            return;
        }
        await this.fetchAll(nameds);
        this.referencedByMap.clear();
        for (const named of nameds) {
            // restore the state
            if (existingStateMap.has(this._identityService.identity(named))) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (named.extension._state$ as any).state = existingStateMap.get(this._identityService.identity(named));
            }

            if (ModelMarker.isDeleted(named) || !named.isOk) {
                continue;
            }

            const dependentNamedSet = Walker.directConceptDependencies(this._modelAttributeContext, named);
            dependentNamedSet.forEach((dependentNamed: Named) => {
                const dendentNamedIdentity = this._identityService.identity(dependentNamed);
                if (this.referencedByMap.has(dendentNamedIdentity)) {
                    if (!this.referencedByMap.get(dendentNamedIdentity).has(named)) {
                        this.referencedByMap.get(dendentNamedIdentity).add(named);
                    }
                } else {
                    const newDependentSet = new Set<Named>();
                    newDependentSet.add(named);
                    this.referencedByMap.set(dendentNamedIdentity, newDependentSet);
                }
            });
        }
    }

    private _nextSubjectIndex(): number {
        return ++this._currentSubjectIndex;
    }

    /*
        Synchronize is called at init time.
    */
    private async synchronizeInternal(resources?: IFullResource[]): Promise<void> {
        const incrementalLoad = !!resources;
        return this._createAndFetchEntities(resources, incrementalLoad, 'synchronizeInternal');
    }

    private async _createAndFetchEntities(
        resources?: IFullResource[],
        incrementalLoad?: boolean,
        callerNameForLogging?: string,
        createFromCustomResources: boolean = false
    ): Promise<void> {
        this.logger.log(ModelService.className, `_createAndFetchEntities ${callerNameForLogging} - start`);
        const nameds: Named[] = [];
        if (!resources) {
            this.logger.log(ModelService.className, `_createAndFetchEntities ${callerNameForLogging} - no resources`);
            const resourceTypes = Metamodel.concepts().map((concept) => {
                return concept.name;
            });
            resources = await this._resourceFilesProvider.listResources(...resourceTypes);
            this.logger.log(ModelService.className, `_createAndFetchEntities ${callerNameForLogging} - listed resources`, { totalResources: resources.length });
        }

        let hasFolders = false;

        for (const resource of resources) {
            let resourceId: string;
            let model: IModel;
            let loadingError: LoadingStatus;
            try {
                resourceId = this._identityService.generateIdentifierFromResource(resource);
                if (incrementalLoad) {
                    // If a named was already created for the resource, the resource was previously downloaded because it was a dependent
                    if (this.identityToObjectMap.has(resourceId)) {
                        continue;
                    }
                }
                model = ModelFetchUtils.getModelByResourceType(resource.type, resource.subType);
            } catch (error) {
                if (resource.type.toLowerCase() === Metamodel.modelFor(IntegrationRuntime).name.toLowerCase()) {
                    // For IR if subtype is bad/unknown then it can throw an error, because Unknown type is not
                    // defined for IR. Handle IR separately, create a model with undefined subType
                    this.logger.error(
                        ModelService.className,
                        `${callerNameForLogging} - Failed to find model for the IR resource, creating model with undefined subType. Error: ${convertObjectToString(
                            error
                        )}`,
                        { ResourceId: resourceId }
                    );
                    (resource as IFullResource).status = LoadingStatusEnum.MissingSubType;
                    resource.subType = undefined;
                    model = ModelFetchUtils.getModelByResourceType(resource.type, resource.subType);
                } else {
                    // There was an error creating a model. Ignore this resource
                    this.logger.warn(
                        ModelService.className,
                        `${callerNameForLogging} - Failed to find model for the resource, ignoring this resource. Error: ${convertObjectToString(
                            error
                        )}`,
                        { ResourceId: resourceId }
                    );
                    continue;
                }
            }

            let named: Named;
            try {
                // The following handles the scenario when the subtype does not match. A scenario could be
                // a dataset has a subType of a LS.
                if (resource.type.toLowerCase() !== model.concept.name.toLowerCase()) {
                    // Trying to get model from type/subtype tags.
                    // In dataflow linked service model name is the same as the dataflow resource so need special handling.
                    model = ModelFetchUtils.getModelByTypeTags(resource.type, resource.subType);
                    if (!model) {
                        // resource type mismatch, create a new model with unknown subtype
                        // handle IR seperately as unknown type does not exis for IR.
                        if (resource.type.toLowerCase() === Metamodel.modelFor(IntegrationRuntime).name.toLowerCase()) {
                            resource.subType = undefined;
                        } else {
                            resource.subType = 'unknown';
                            await ModelRegisterationUtils.registerUnknownSubtype(
                                resource.type,
                                resource.subType,
                                this._contextData
                            );
                        }
                        (resource as IFullResource).status = LoadingStatusEnum.MissingSubType;
                        model = ModelFetchUtils.getModelByResourceType(resource.type, resource.subType);
                    }
                }
                named = model.create<Named>();
            } catch (error) {
                // There was a problem creating a new named. Ignore this resource.
                this.logger.error(
                    ModelService.className,
                    `${callerNameForLogging} - Error creating new named, ignoring this resource. ResourceId: ${resourceId}. Error: ${convertObjectToString(
                        error
                    )}`
                );
                continue;
            }

            try {
                if (
                    [
                        Pipeline.MODEL.name,
                        AirflowEntity.MODEL.name,
                        Dataset.MODEL.name,
                        DataFlow.MODEL.name,
                        WranglingDataflow.MODEL.name,
                        Flowlet.MODEL.name,
                        UserDefinedFunctionLibrary.MODEL.name,
                        AdfDataMapper.MODEL.name
                    ].includes(resource.type)
                ) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (named as any).folder.name = resource.folderName;
                    if (resource.folderName && !hasFolders) {
                        hasFolders = true;
                        this.logger.log(ModelService.className, `${callerNameForLogging} - User has folders`);
                    }
                }
            } catch (error) {
                this.logger.error(
                    ModelService.className,
                    `${callerNameForLogging} - Unable to set the folder name. ResourceId: ${resourceId}. Error: ${convertObjectToString(
                        error
                    )}`
                );
                loadingError = LoadingStatusEnum.UnknownError;
                named.status = 'Bad';
                named.loadError.error = error;
            }

            if (resource.isV2) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (named as any).isV2 = resource.isV2;
            }
            switch (resource.status) {
                case 'Ok':
                case 'Stub':
                    named.status = 'Ok';
                    named.loadError.status = loadingError;
                    break;
                default:
                    named.status = 'Bad';
                    named.loadError.status = resource.status;
                    break;
            }
            named.name = resource.name;
            named.objectId = resource.objectId;
            named.workspaceObjectId = resource.workspaceObjectId;
            this._identityService.setIdentity(named, resourceId);

            nameds.push(named);
            if (incrementalLoad) {
                markListed(named);
                this.idToObjectMap.set(named.id, named);
                this.identityToObjectMap.set(this._identityService.identity(named), named);
                this.subject.next({ index: this._nextSubjectIndex(), subject: named, added: true });
            }
        }

        if (!incrementalLoad) {
            if (!createFromCustomResources) {
                this.idToObjectMap.forEach((value, name) => {
                    this.subject.next({ index: this._nextSubjectIndex(), subject: value, added: false });
                });
                this.idToObjectMap.clear();
                this.identityToObjectMap.clear();
                nameds.forEach((named) => {
                    markListed(named);
                    this.idToObjectMap.set(named.id, named);
                    this.identityToObjectMap.set(this._identityService.identity(named), named);
                    this.subject.next({ index: this._nextSubjectIndex(), subject: named, added: true });
                });

                await this.fetchAll(nameds);
                this._isFetching.next(0);
                const allUnknownTypes = Metamodel.models()
                    .filter((item) => item?.createdFromUnknownType)
                    .map((item) => item.name)
                    .join(',');
                this.logger.log(
                    ModelService.className,
                    `${callerNameForLogging} - finished, total unknown model types`,
                    { allUnknownTypes }
                );
            } else {
                nameds.forEach((named) => {
                    markListed(named);
                    this.idToObjectMap.set(named.id, named);
                    this.identityToObjectMap.set(this._identityService.identity(named), named);
                });
                // fetch from the custom resources
                const fetchPromises = [];
                for (const [index, named] of nameds.entries()) {
                    named.extension.createdFromResourceFile = true;
                    fetchPromises.push(this.fetchWithCustomResource(named, resources[index].payload));
                }
                await Promise.all(fetchPromises);
                // mark all resources as new state and commit
                nameds.forEach((item) => {
                    this.idToObjectMap.delete(item.id);
                    this.identityToObjectMap.delete(this._identityService.identity(item));
                    this._identityService.identify(item);
                    this._modelMarkerWrapper.markNew(item);
                    this.commit(item);
                });
            }
        }
        this.logger.log(ModelService.className, `_createAndFetchEntities ${callerNameForLogging} - end`);
    }

    /*
        TODO: Need to build dependency graph with weak and strong edges
    */
    private unfetchDeep(named: Named, unfetchedNameds: Set<Named>): void {
        // If a model is marked bad, then it was not fetched properly earlier.
        // We need to clean a bad named as well.
        if (named.isOk && ModelMarker.isListed(named)) {
            return;
        }

        // TODO: Once we build a live dependency graph this should be faster
        this.idToObjectMap.forEach((dependent) => {
            if (
                dependent !== named &&
                !ModelMarker.isListed(dependent) &&
                !ModelMarker.isDeleted(dependent) &&
                Walker.directConceptDependencies(this._modelAttributeContext, dependent).has(named)
            ) {
                this.unfetchDeep(dependent, unfetchedNameds);
            }
        });

        this.unfetch(named);
        unfetchedNameds.add(named);
        markListed(named);
    }

    /*
        Fetches the details of the pipeline if not already fetched.
    */
    private async _fetchInternal<T extends Named>(
        context: AdapterContext,
        named: T,
        globalScopeNamedMap?: Map<string, Named>,
        customResource?: string,
        force?: boolean
    ): Promise<T> {
        if (!customResource && (named.isBad || !ModelMarker.isListed(named)) && !force) {
            // with custom resource, we should fetch all the time. No need to fetch if named is already marked bad.
            // Also, if named is listed, return faster without adding and removing from set.
            if (!named.isOk && !customResource) {
                // for a bad resource, the shadow has not been casted, cast it now.
                this.tryCastShadowForBadObject(named);
            }
            return named;
        }
        if (context.adapterConfig) {
            context.adapterConfig.replaceIdentitiesForNonConcepts = true;
        } else {
            context.adapterConfig = { contextData: this._contextData, replaceIdentitiesForNonConcepts: true };
        }
        Object.assign(context.adapterConfig, { ...this._sharedAdapterContext });
        // when fetching from customResource, we don't take cached promise from this.idToPromiseFetchQueue
        const promise =
            customResource || force
                ? this.fetchOne(context, named, globalScopeNamedMap, customResource, force)
                : this.idToPromiseFetchQueue.get(named.id) || this.fetchOne(context, named, globalScopeNamedMap);
        this.idToPromiseFetchQueue.set(named.id, promise);
        try {
            await promise;
            return promise as Promise<T>;
        } catch (err) {
            const resourceId = this._identityService.identity(named);
            if (
                !(
                    named.isFakeEntityCreatedFromReference &&
                    context.adapterConfig.tolerateInvalidReference &&
                    err.message === format(DEDICATED_NOTFOUND_ENTITY_ERROR_TEMPLATE, named.objectId)
                )
            ) {
                const logString = JSON.stringify({
                    resourceId,
                    message: err.message,
                    stack: err.stack
                });
                // Note: err may not be an Error class.
                const isWarning: boolean = err.message?.includes('Unexpected end of JSON input');
                if (isWarning) {
                    this.logger.warn(
                        ModelService.className,
                        `fetchInternal failed. ${logString} ${err.message ? '' : convertObjectToString(err)}`
                    );
                } else {
                    this.logger.error(
                        ModelService.className,
                        `fetchInternal failed. ${logString} ${err.message ? '' : convertObjectToString(err)}`
                    );
                }
                // fetch failed, mark this named as bad
                named.status = 'Bad';
                named.loadError.status = LoadingStatusEnum.UnknownError;
                named.loadError.error = err;
            }
        } finally {
            this.idToPromiseFetchQueue.delete(named.id);

            if (named.status === 'Ok') {
                this._checkifSubNamedIsBad(context, named);
            }
            if (!named.isOk && !customResource) {
                // for a bad resource, the shadow has not been casted, cast it now.
                this.tryCastShadowForBadObject(named);
            }
        }

        return named;
    }

    private tryCastShadowForBadObject(named: Named): void {
        try {
            Shadow.cast(this._modelAttributeContext, named);
        } catch (e) {
            this.logger.error(
                ModelService.className,
                `Error on cast shadow for bad resource ${this._identityService.identity(
                    named
                )}. Reason: ${convertObjectToString(e)}`
            );
        }
    }

    private _checkifSubNamedIsBad(context: AdapterContext, named: Named): void {
        let isAnyReferenceNamedBad: boolean = false;
        let isAnyContainedNameBad: boolean = false;

        Walker.walk(
            this._modelAttributeContext,
            (object, property, value, attribute, parameters) => {
                if (!value) {
                    return Direction.SKIP;
                }
                if (Metamodel.isModelOf(value) && attribute) {
                    const valueModel = Metamodel.modelOf(value);
                    const namedValue = value as Named;
                    if (namedValue && valueModel.concept && !namedValue.isOk) {
                        // Found a bad sub-named. Stop and return
                        if (attribute.isReferenced(context.adapterConfig)) {
                            isAnyReferenceNamedBad = true;
                        } else {
                            isAnyContainedNameBad = true;
                        }
                        return Direction.STOP;
                    }
                }
                return Direction.GO;
            },
            DefaultPropertyInspector.instance,
            named
        );

        // Mark the fetched named 'BadDependency' if it contains references to any bad or partially bad nameds.
        // or mark it Bad if it contains bad nested entities
        if (isAnyReferenceNamedBad) {
            named.status = 'BadDependency';
            named.loadError.status = LoadingStatusEnum.UnknownError;
        } else if (isAnyContainedNameBad) {
            named.status = 'Bad';
            named.loadError.status = LoadingStatusEnum.UnknownError;
        }
    }

    /*
        Fetches the details of the resource if not already fetched.
    */
    private async fetchOne<T extends Named>(
        context: AdapterContext,
        named: T,
        globalScopeNamedMap?: Map<string, Named>,
        customResource?: string,
        force?: boolean
    ): Promise<T> {
        if (!globalScopeNamedMap) {
            globalScopeNamedMap = new Map<string, Named>();
        }

        // Fetch only listed named.
        if (!customResource && !ModelMarker.isListed(named) && !force) {
            return named;
        }

        const concept = Metamodel.modelOf(named).concept;
        const model = Metamodel.modelOf(named);
        if (!concept) {
            this.logger.error(
                model.name,
                `Fetch failed for named: ${this._identityService.identity(
                    named
                )}. Cannot fetch non-concept type. Type: ${model.name}.`
            );
            throw new Error(
                `Fetch failed for named: ${this._identityService.identity(
                    named
                )}. Cannot fetch non-concept type. Type: ${model.name}.`
            );
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let adapterModel: IModelAdapter<any, any> | IModelAdapterAsync<any, any>;
        if (this._resourceFilesProvider && named.name) {
            // If custom resource is not provided, get the resource from resourceFileProvider
            const resourceString =
                customResource ||
                (await this._resourceFilesProvider.getResourceJSON({
                    name: named.name,
                    type: concept.name,
                    objectId: named.objectId,
                    workspaceObjectId: named.workspaceObjectId,
                    subType: this._getNamedSubType(model, concept)
                }));
            if (!!resourceString) {
                const resource = JSON.parse(resourceString);
                if (context.isAsyncAdapter(model)) {
                    adapterModel = context.asyncAdapterFor(model);
                    if (!adapterModel) {
                        this.logger.error(
                            model.name,
                            `Fetch failed for named: ${this._identityService.identity(
                                named
                            )}. Adapter not found. Type: ${model.name}.`
                        );
                        throw new Error(
                            `Fetch failed for named: ${this._identityService.identity(
                                named
                            )}. Adapter not found. Type: ${model.name}.`
                        );
                    }
                    await adapterModel.toModel(resource, named);
                } else {
                    adapterModel = context.adapterFor(model);
                    if (!adapterModel) {
                        this.logger.error(
                            model.name,
                            `Fetch failed for named: ${this._identityService.identity(
                                named
                            )}. Adapter not found. Type: ${model.name}.`
                        );
                        throw new Error(
                            `Fetch failed for named: ${this._identityService.identity(
                                named
                            )}. Adapter not found. Type: ${model.name}.`
                        );
                    }
                    adapterModel.toModel(resource, named);
                }
            }
        }

        if (Constants.GlobalVariables.isAutosaveSupportType(concept.name as Constants.ResourceType)) {
            const states = await this._resourceFilesProvider.getResourceStateBySource(
                this._identityService.identity(named),
                concept.name as Constants.ResourceType
            );
            states.forEach((state, index) => {
                this._modelMarkerWrapper.bulkMark([named], state, index === 0 ? true : false); // erase the original state, which is listed
            });
            // For the one from user store, set hasFetchedCompletely to true to avoid fetching info from artifact service
            if (states.includes(eModelState.SAVED) && named instanceof Notebook) {
                named.hasFetchedCompletely = true;
            }
        } else {
            // mark it fetched, we do not want to get into infinite loop when doing secondary walk due to cyclic references.
            this._modelMarkerWrapper.markFetched(named);
        }

        // referencedNameds contains the referenced nameds and they need to be fecthed as well (recursively).
        const referencedNamedsList = new Set<Named>();
        let secondaryWalk = false;

        // With incremental loading, the referenced resources may not be downloaded yet, we need to download these
        // resources on-demand, create named and then substitute proxy.
        // referencedResourcePropertySet keeps the properties which are needed
        // for substituting proxy once the referenced reourcs are downloaded and nameds created.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const referencedResourcePropertySet = new Set<ReferencedProxyNamedInfo>();

        // Go through each and every property of the named (recursively), to get all the referenced resources.
        // The walker takes following arguments:
        // 1. object: The object whose properties are currently being scanned.
        // 2. property: Name of the property/referenced object type, currently being scanned.
        // 3. value: Actual property/referenced object, being scanned.
        // 4. attribute: The attribute/annotations of the property/referenced object.
        Walker.walk(
            this._modelAttributeContext,
            (object, property, value, attribute, parameters) => {
                if (!value) {
                    return Direction.SKIP;
                }
                if (!Metamodel.isModelOf(value) || !attribute || !attribute.isReferenced(context.adapterConfig)) {
                    return Direction.GO;
                }
                const valueModel = Metamodel.modelOf(value);
                const valueNamed = value as Named;

                // Skip the referenced object that are not in global scope. Non-concept global scope are implicitly skipped by the walker.
                // But there could be a mutual reference in the same document. So we don't want to be searching around for this
                if (
                    !valueModel.globalScope ||
                    valueNamed.conceptOwner === named ||
                    ((context.adapterConfig.inlineDataset || context.adapterConfig.convertToFabric) && valueModel.concept?.name === ModelConstants.dataset)
                ) {
                    return Direction.GO;
                }
                if (!ModelMarker.isProxy(value)) {
                    return Direction.SKIP;
                }

                const valueNamedIdentity = this._identityService.identity(valueNamed);
                if (!valueNamedIdentity) {
                    throw new Error(
                        `Fetch failed for named: ${this._identityService.identity(
                            named
                        )}. Unnamed reference found, reference type: ${property}`
                    );
                }

                const conceptNamed = !valueModel.concept ? (valueNamed.conceptOwner as Named) : valueNamed;
                if (!conceptNamed) {
                    this.logger.error(
                        model.name,
                        `Fetch failed for named: ${this._identityService.identity(
                            named
                        )}. Concept owner is not set for: ${valueNamedIdentity}`
                    );
                    throw new Error(
                        `Fetch failed for named: ${this._identityService.identity(
                            named
                        )}. Concept owner is not set for: ${valueNamedIdentity}`
                    );
                }

                const conceptIdentity = this._identityService.identity(conceptNamed);
                if (!conceptNamed.name && !conceptIdentity) {
                    this.logger.error(
                        model.name,
                        `Fetch failed for named: ${this._identityService.identity(
                            named
                        )}. Unnamed concept owner for : ${valueNamedIdentity}`
                    );
                    throw new Error(
                        `Fetch failed for named: ${this._identityService.identity(
                            named
                        )}. Unnamed concept owner for : ${valueNamedIdentity}`
                    );
                }

                referencedResourcePropertySet.add(
                    new ReferencedProxyNamedInfo(conceptIdentity, object, property, valueNamed, parameters, attribute)
                );
                return Direction.GO;
            },
            DefaultPropertyInspector.instance,
            named
        );

        // Get the referenced named list.
        for (const referencedResourceProperty of referencedResourcePropertySet) {
            const referencedResourceIdentity = referencedResourceProperty.referencedResourceIdentity;
            const referencedValueNamed = referencedResourceProperty.proxyNamed as Named;
            try {
                const referencedNamed = await this._getNamedByIdentity(
                    referencedResourceIdentity,
                    true,
                    referencedValueNamed?.name,
                    {
                        workspaceObjectId: referencedValueNamed?.workspaceObjectId ?? this._contextData.contextId,
                        resourceSubType:
                            referencedValueNamed instanceof LinkedService
                                ? referencedValueNamed.tridentArtifactType
                                : undefined
                    }
                );
                if (referencedNamed) {
                    referencedNamedsList.add(referencedNamed);
                    // named identifier may be changed from objectId to name, here to ensure referencedResourceIdentity is also up-to-date
                    referencedResourceProperty.referencedResourceIdentity =
                        this._identityService.identity(referencedNamed);
                }
            } catch (err) {
                if (
                    !(
                        context.adapterConfig.tolerateInvalidReference &&
                        err.message === format(DEDICATED_NOTFOUND_ENTITY_ERROR_TEMPLATE, referencedValueNamed?.name)
                    )
                ) {
                    throw err;
                }
            }
        }

        // Fetch all the referenced nameds
        const fetchPromises = new Array<Promise<Named>>();
        referencedNamedsList.forEach((referencedNamed) =>
            fetchPromises.push(this._fetchInternal(context, referencedNamed, globalScopeNamedMap))
        );
        await Promise.all(fetchPromises);

        // All the referenced resources have been downloaded and fetched, now substitute the proxy and handle non-concept nameds
        for (const referencedResourceProperty of referencedResourcePropertySet) {
            let referencedNamed = await this._getNamedByIdentity(referencedResourceProperty.referencedResourceIdentity);
            const referenceIsEmpty = !referencedNamed;
            if (
                context.adapterConfig.tolerateInvalidReference &&
                (referenceIsEmpty || referencedNamed.isFakeEntityCreatedFromReference)
            ) {
                referencedNamed = referencedResourceProperty.proxyNamed;
                ModelMarker.markFetched(referencedNamed);
                if (referenceIsEmpty) {
                    // do not repeatedly add invalid referenced named
                    this.addToObjectMap(referencedNamed);
                    this.subject.next({ index: this._nextSubjectIndex(), subject: referencedNamed, added: true });
                }
            } else if (referenceIsEmpty) {
                this.logger.error(ModelService.className, `reference is empty`, {
                    parentObjectName: referencedResourceProperty?.parentObject?.name,
                    parentObjectConcept: Metamodel.modelOf(referencedResourceProperty?.parentObject)?.concept?.name,
                    referencedObjectName: referencedResourceProperty?.proxyNamed?.name,
                    referencedObjectConcept: Metamodel.modelOf(referencedResourceProperty?.proxyNamed)?.concept?.name
                });
            }
            const valueNamed = referencedResourceProperty.proxyNamed;
            const valueModel = Metamodel.modelOf(valueNamed);
            const valueNamedIdentity = this._identityService.identity(valueNamed);
            if (!valueModel.concept) {
                if (globalScopeNamedMap.has(valueNamedIdentity)) {
                    // If globalScopeNamedMap already contains the object.
                    referencedNamed = globalScopeNamedMap.get(valueNamedIdentity);
                } else {
                    // This is non-concept, its proxy will be substituted in secondary walk.
                    secondaryWalk = true;
                    continue;
                }
            }

            this.substituteProxy(
                referencedResourceProperty.parentObject,
                referencedResourceProperty.propertyName,
                referencedResourceProperty.proxyNamed,
                referencedNamed,
                referencedResourceProperty.parameters,
                referencedResourceProperty.attribute
            );
        }

        if (secondaryWalk) {
            for (const referencedNamed of referencedNamedsList) {
                const conceptIdentity = this._identityService.identity(referencedNamed);
                if (conceptIdentity) {
                    globalScopeNamedMap.set(conceptIdentity, referencedNamed);
                    this.mapGlobalScopeNameds(context, referencedNamed, globalScopeNamedMap);
                }
            }

            Walker.walk(
                this._modelAttributeContext,
                (object, property, value, attribute, parameters) => {
                    if (!value) {
                        return Direction.SKIP;
                    }
                    if (!Metamodel.isModelOf(value) || !attribute || !attribute.isReferenced(context.adapterConfig)) {
                        return Direction.GO;
                    }
                    const valueModel = Metamodel.modelOf(value);
                    const namedValue = value as Named;

                    if (
                        !valueModel.globalScope ||
                        namedValue.conceptOwner === named ||
                        (context.adapterConfig.inlineDataset && valueModel.concept?.name === ModelConstants.dataset)
                    ) {
                        return Direction.GO;
                    }
                    if (!ModelMarker.isProxy(value)) {
                        return Direction.SKIP;
                    }

                    const identity = this._identityService.identity(namedValue);
                    if (!identity || !globalScopeNamedMap) {
                        this.logger.error(
                            model.name,
                            `Fetch failed for named: ${this._identityService.identity(
                                named
                            )}. Unnamed reference found in secondary pass, reference type: ${model.name}`
                        );
                        throw new Error(
                            `Fetch failed for named: ${this._identityService.identity(
                                named
                            )}. Unnamed reference found in secondary pass, reference type: ${model.name}`
                        );
                    }

                    this.substituteProxy(
                        object,
                        property,
                        namedValue,
                        globalScopeNamedMap.get(identity),
                        parameters,
                        attribute
                    );
                    return Direction.GO;
                },
                DefaultPropertyInspector.instance,
                named
            );
        }

        // Call postToModel here, some model props need to be populated based on all fetched references
        // e.g. linked service's auth type depends on fetched credential type
        if (adapterModel) {
            await adapterModel.postToModel(named);
        }

        if (!customResource) {
            // Don't cast the shadow when fetch is being called with custom resources. We don not want to have the shadow same as
            // resource when user has changed the JSON
            Shadow.cast(this._modelAttributeContext, named);
            if (Constants.GlobalVariables.enableAutosave) {
                AutosaveShadow.cast(this._modelAttributeContext, named);
            }
        }
        // Clear up the dependency.
        // TODO: We need to refactor dependency, we can use dependency to unfetch to fix leak.
        Dependency.depend(named);

        // handle rename action stored in autostore
        if (
            Constants.GlobalVariables.isAutosaveSupportType(concept.name as Constants.ResourceType) &&
            ModelMarker.isFetched(named) &&
            ModelMarker.isDirty(named)
        ) {
            const newName = this._resourceFilesProvider.getRenamedName(
                concept.name as Constants.ResourceType,
                named.name
            );
            if (newName) {
                named.name = newName;
            }
        }
        return named;
    }

    /**
     * Load dynamic connector configuration and register corresponding models
     */
    private async loadDynamicConnectors(): Promise<void> {
        if (__IS_FABRIC_DPE__ || __IS_FABRIC_MDF__ || this._isDedicatedIFrame) {
            return;
        }
        const startMessage = format(
            stringResources.ConnectorMetadata_LogMessageTemplate,
            'Start registering dynamic connectors'
        );
        this.logger.log(ModelService.className, startMessage);
        try {
            await this._dynamicConnectorService.addDynamicModelAsync();
        } catch (error) {
            // log error and go, since it should not impact static connectors
            this.logger.error(
                ModelService.className,
                format(
                    stringResources.ConnectorMetadata_LogMessageTemplate,
                    `Error happens when loading dynamic connectors: ${convertObjectToString(error, false)}`
                )
            );
        }
        this.logger.log(
            ModelService.className,
            format(stringResources.ConnectorMetadata_LogMessageTemplate, `Finished registering dynamic connectors`)
        );
    }

    private _getNamedSubType(model: IModel, concept: IModel): string {
        return model !== concept ? (model.tag('subType') ? model.tag('subType') : model.name) : undefined;
    }

    private substituteProxy(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        object: any,
        property: string,
        current: Named,
        substitute: Named | undefined,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        parameters: any[],
        attribute?: IAttribute
    ): void {
        const propValue = object[property];
        if (Array.isArray(propValue)) {
            // Remove the old entry and replace with existing one
            const array = propValue as Named[];
            if (substitute) {
                array[parameters[0] as number] = substitute;
            } else {
                array.splice(parameters[0] as number, 1);
            }
        } else if (isSet(propValue)) {
            const aSet = propValue as Set<Named>;
            aSet.delete(current);
            if (substitute) {
                // Else the reference will be forever broken
                aSet.add(substitute);
            }
        } else if (isMap(propValue)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const aMap = propValue as Map<any, any>;
            const key = parameters[0];
            // Ideally you never map from one object to itself?
            if (current === key) {
                const value = aMap.get(key);
                aMap.delete(key);
                if (substitute && value) {
                    // Else the reference will be forever broken
                    aMap.set(substitute, value);
                }
            } else {
                // We don't allow undefined maps
                if (substitute) {
                    aMap.set(key, substitute);
                } else {
                    aMap.delete(key);
                }
            }
        } else {
            object[property] = substitute; // Rewire it even if it is listed. Can become undefined
        }

        if (attribute && attribute.referenceUpdate) {
            attribute.referenceUpdate(object);
        }
    }

    private mapGlobalScopeNameds(context: AdapterContext, named: Named, globalScopeNamedMap: Map<string, Named>): void {
        Walker.walk(
            this._modelAttributeContext,
            (_object, _property, value, attribute) => {
                if (!value) {
                    return Direction.SKIP;
                }
                // Only look at contained objects
                if (attribute?.isReferenced(context.adapterConfig) || !Metamodel.isModelOf(value)) {
                    return Direction.SKIP;
                }

                const valueModel = Metamodel.modelOf(value);

                if (!valueModel.globalScope) {
                    return Direction.GO;
                }
                const namedValue = value as Named;
                const identity = this._identityService.identity(namedValue);
                if (!identity) {
                    return Direction.GO;
                }
                globalScopeNamedMap.set(identity, namedValue);
                return Direction.GO;
            },
            this.propertyInspector,
            named
        );
    }

    /**
     * Fetch an object by identity. Support downloading resource from metadata service and committing to model service if the model doesn't exist
     * @param resourceIdentity model identity used to track model in identityToObjectMap and identityToCommittedObjectMap
     * @param downloadIfNotPresent download and create if model doesn't exist, default to false
     * @param uniqueKey resource unique id used in metadata service, can be resource name (in ADF/Synapse) or objectId (in Trident)
     * @param resourceSubType resource sub type. Required when the resource associates to a linked service model
     * @returns named object
     */
    private async _getNamedByIdentity(
        resourceIdentity: string,
        downloadIfNotPresent?: boolean,
        uniqueKey?: string,
        options?: {
            workspaceObjectId?: string;
            resourceSubType?: string;
        }
    ): Promise<Named> {
        let named: Named =
            this.identityToObjectMap.get(resourceIdentity) || this.identityToCommittedObjectMap.get(resourceIdentity);
        if (!named && downloadIfNotPresent) {
            const split = resourceIdentity.split('$$');
            const resourceType = split[0] as Constants.ResourceType;
            uniqueKey = stringIsNullOrEmpty(uniqueKey) ? split[1] : uniqueKey;
            // Note: resource type here is actually in lower case.
            const resource = await this._resourceFilesProvider.getResourceObject(
                resourceType,
                uniqueKey,
                false,
                options
            );
            if (resource) {
                await this.synchronizeInternal([resource]);
                named = this.identityToObjectMap.get(resourceIdentity);
            }
        }
        return named;
    }

    private identify(nameds: Set<Named>): void {
        for (const named of nameds) {
            // We can still work with whatever exists as a identity
            if (ModelMarker.isDeleted(named)) {
                continue;
            }
            this._identityService.identify(named);

            Walker.walk(
                this._modelAttributeContext,
                (_object, _property, value, attribute) => {
                    if (!value) {
                        return Direction.SKIP;
                    }
                    if (!Metamodel.isModelOf(value)) {
                        return Direction.GO;
                    }

                    if (attribute?.isReferenced(this._modelAttributeContext)) {
                        return Direction.SKIP;
                    }
                    const valueModel = Metamodel.modelOf(value);
                    if (valueModel.globalScope) {
                        this._identityService.identify(value as Named);
                    }
                    return Direction.GO;
                },
                DefaultPropertyInspector.instance,
                named
            );
        }
    }

    private async _fetchNotebookResource(named: Named): Promise<string | undefined> {
        if (ModelMarker.isNew(named) || (named as Notebook).hasFetchedCompletely) {
            // if notebook is new or has fetched complete content which has been published, returning undefined to maintain original logic
            return undefined;
        }

        // for a saved named, mark hasFetchedCompletely as true
        if (ModelMarker.isDirty(named) && Constants.GlobalVariables.enableAutosave) {
            (named as Notebook).hasFetchedCompletely = true;
            return undefined;
        }

        // contains payload
        const fetchedResource = await this._resourceFilesProvider.getResourceObject(
            'notebook',
            this._identityService.getUniqueKeyFromNamed(named)
        );
        const payload = JSON.parse(fetchedResource.payload);

        // copied from BaseResourceProvider.getResourceJSON
        delete payload.etag;

        if (payload.properties && payload.properties.tags) {
            delete payload.properties.tags;
        }

        // to fix Bug 3466717: unexpectedly overrides the notebook folder to its original state.
        if (payload.properties?.folder?.name && (named as Notebook).folder?.name) {
            payload.properties.folder.name = (named as Notebook).folder.name;
        }

        (named as Notebook).hasFetchedCompletely = true;
        return JSON.stringify(payload);
    }

    private async _getNamedListPerType(
        baseType: Constants.ResourceType,
        parentResourceType?: Constants.ResourceType,
        parentName?: string,
        excludedNameds?: Named[]
    ): Promise<Named[]> {
        // filter by base type first
        const nameds: Named[] = await lastValueFrom(
            this.list(undefined, Metamodel.modelByName(baseType)).pipe(timeout({ each: 5, with: () => from([]) })),
            { defaultValue: [] }
        );

        if (parentResourceType && parentName) {
            // filter by parentName then
            return nameds.filter(
                (value) =>
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (value as any).parentInfo &&
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (value as any).parentInfo.resourceName &&
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (value as any).parentInfo.resourceName.toLowerCase() === parentName.toLowerCase()
            );
        } else if (excludedNameds) {
            return nameds.filter((item) => excludedNameds.indexOf(item) === -1);
        }
        return nameds;
    }
}
